import { Component, OnDestroy, OnInit, AfterViewInit, effect, inject, PLATFORM_ID, computed, signal, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialsPillsComponent } from '../shared/materials-pills/materials-pills.component';
import { RecyclingFiltersComponent } from '../shared/recycling-filters/recycling-filters.component';
import { MapLibreMapComponent } from '../shared/maplibre-map/maplibre-map.component';
import { HorizontalPagePickerComponent } from '../shared/horizontal-page-picker/horizontal-page-picker.component';
import { SwitchPointCardComponent } from '../shared/switch-point-card/switch-point-card.component';
import { PlaceModalComponent } from '../shared/place-modal/place-modal.component';
import { ContainerModalComponent } from '../shared/container-modal/container-modal.component';
import { RouteModalComponent } from '../shared/route-modal/route-modal.component';
import { EventModalComponent } from '../shared/event-modal/event-modal.component';
import { ContextMenuComponent, ContextMenuOption } from '../shared/context-menu/context-menu.component';
import { RouteEntity } from '../shared/route-card/route-card.component';
import { CreateNewPointComponent } from '../shared/create-new-point/create-new-point.component';
import { ResultsSummaryComponent, TypeCount } from '../shared/results-summary/results-summary.component';
import { NearbyResultComponent } from '../shared/nearby-result/nearby-result.component';
import { TypeTogglesComponent } from '../shared/type-toggles/type-toggles.component';
import { materialsState } from '../../state/materials.state';
import { pointTypesState } from '../../state/point-types.state';
import { capabilityTypesState } from '../../state/capability-types.state';
import { mapEventsState } from '../../state/map-events.state';
import { ProgrammingModeService } from '../../shared/services/programming-mode.service';
import { ProgrammingPanelComponent } from '../shared/programming-panel/programming-panel.component';
import { CustomModalController } from '../../shared/services/custom-modal-controller.service';
import { LocationService } from '../../shared/services/location.service';
import { GeocodingService } from '../../shared/services/geocoding.service';
import { FilterService } from '../../shared/services/filter.service';
import { PointsApiService } from '../../shared/services/points-api.service';
import { PointCreationService } from '../../shared/services/point-creation.service';
import { RecyclingPoint } from '../../types/recycling-point.interface';
import { Material } from '../../types/material.interface';
import { isDesktopViewport } from '../../shared/utils/viewport.utils';
import { TycComponent } from '../shared/tyc/tyc.component';
import { CustomPopoverService } from '../../shared/services/custom-popover.service';
import { PosthogService } from '../../shared/services/posthog.service';
import { StorageService, STORAGE_KEYS } from '../../shared/services/storage.service';
import { PointsCacheService } from '../../shared/services/points-cache.service';
import { PlatformService } from '../../shared/services/platform.service';
import { UiSettingsService } from '../../shared/services/ui-settings.service';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { TranslationService } from '../../shared/services/translation.service';
import { ScreenLayer } from '../../shared/enums/screen-layer.enum';
import { ModalDomain } from '../../shared/enums/modal-domain.enum';

@Component({
  selector: 'app-home',
  standalone: true,
  // MapLibreMapComponent is deferred via @defer in template - but must be in imports for AOT
  imports: [CommonModule, FormsModule, MaterialsPillsComponent, RecyclingFiltersComponent, MapLibreMapComponent, HorizontalPagePickerComponent, SwitchPointCardComponent, ProgrammingPanelComponent, CreateNewPointComponent, NearbyResultComponent, TypeTogglesComponent, TranslatePipe],
  templateUrl: './home.html',
  styleUrl: './home.scss'
})
export class Home implements OnInit, OnDestroy, AfterViewInit {
  private platformId = inject(PLATFORM_ID);
  private platform = inject(PlatformService);
  public isBrowser = this.platform.isBrowser;
  private programmingModeService = inject(ProgrammingModeService);
  private cdr = inject(ChangeDetectorRef);
  private customModalCtrl = inject(CustomModalController);
  private customPopoverService = inject(CustomPopoverService);
  private locationService = inject(LocationService);
  private pointsApiService = inject(PointsApiService);
  private pointCreationService = inject(PointCreationService);
  public filterService = inject(FilterService);
  private posthog = inject(PosthogService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private geocodingService = inject(GeocodingService);
  private storage = inject(StorageService);
  private http = inject(HttpClient);
  private uiSettings = inject(UiSettingsService);
  private t = inject(TranslationService);
  private pointsCache = inject(PointsCacheService);

  // Loading state for points
  isLoadingPoints = signal(false);

  // "Buscar acá" button state (list-level: shows when viewport moved since last load)
  showSearchHereButton = signal(false);
  private checkMapPointInterval?: any;
  private lastCheckedPoint?: { lat: number; lng: number };

  // Tracks the location query used in the last API load (to detect if map moved)
  private lastLoadedLocation: { lat: number; lng: number; radiusKm: number } | null = null;

  // Current map viewport center + radius for location-aware queries
  private mapLocation = signal<{ lat: number; lng: number; radiusKm: number } | null>(null);
  // Raw viewport bounds for client-side marker filtering (viewportTypeCounts)
  private mapBounds = signal<{ north: number; south: number; east: number; west: number } | null>(null);
  private mapBoundsDebounceTimer?: ReturnType<typeof setTimeout>;

  // Nearby search radius in kilometers
  private readonly NEARBY_SEARCH_RADIUS_KM = 10;

  public initClass: boolean = false;
  // ViewChild reference to map component
  @ViewChild(MapLibreMapComponent) mapComponent!: MapLibreMapComponent;

  // ViewChild reference to recycling filters component
  @ViewChild(RecyclingFiltersComponent) recyclingFiltersComponent!: RecyclingFiltersComponent;

  // ViewChild reference to list scroll container
  @ViewChild('listInnerScroll') listScrollContainer!: ElementRef<HTMLElement>;

  // ViewChild reference to results section for scroll monitoring
  @ViewChild('resultsSection', { read: ElementRef }) resultsSection!: ElementRef<HTMLElement>;

  // ViewChild reference to home container for scroll listening
  @ViewChild('homeContainer') homeContainer!: ElementRef<HTMLElement>;
  @ViewChild('wrapperListScroll') wrapperListScroll!: ElementRef<HTMLElement>;

  location: string = '';
  isDropdownOpen = false;

  // Filter state - now using FilterService
  // showOnlyOpen, showContainers, searchTerm moved to FilterService

  // Map fullscreen state - DISABLED: Map always appears as fullscreen in background
  // isMapFullscreen = false;

  mapHeight = 'calc(100dvh - 300px)';

  // Schedule/polygon entities collapsible state
  showroutePoints = signal(false);

  // Location component expanded state
  expandedLocationComponent: boolean = false;

  // Pin mode state - for controlling UI visibility during pin placement
  isPinModeActive: boolean = false;
  private originalFiltersExpanded: boolean = false; // Store original filter state

  // Scroll state tracking for snap behavior
  wrapperScrollTop = signal(0);
  wrapperScrollDirection = signal<'up' | 'down'>('down');

  // Signal to track current view position for CSS classes
  currentViewPositionSignal = signal<'top' | 'bottom'>('bottom');

  // Computed: toggle button text depends on view position
  toggleButtonText = computed(() =>
    this.currentViewPositionSignal() === 'top'
      ? this.t.instant('home.verMapa')
      : this.t.instant('home.verLista')
  );

  // Pagination state
  private itemsPerPage = 20;
  private currentPage = signal(1);
  private totalPages = computed(() => Math.ceil(this.filteredAndSortedRecyclingPoints().length / this.itemsPerPage));
  isFiltersExpanded: boolean = false;

  // Paginated results
  paginatedPoints = computed(() => {
    // return this.recyclingPointsSignal();
    const allPoints = this.filteredAndSortedRecyclingPoints();
    // const allPoints = this.recyclingPointsSignal();
    const page = this.currentPage();
    const startIndex = (page - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return allPoints.slice(startIndex, endIndex);
  });

  // Time testing functionality
  private testTime: Date | null = null;
  private useRealTime: boolean = true;

  // Programming mode functionality - using service signal
  readonly isProgrammingMode = this.programmingModeService.isProgrammingMode;

  // Saved map position from UiSettingsService (restored on app init)
  readonly savedMapPosition = computed(() => {
    const state = this.uiSettings.mapState();
    return { lat: state.lat, lng: state.lng };
  });
  readonly savedMapZoom = computed(() => this.uiSettings.mapState().zoom);

  // Viewport functionality - using global signal
  readonly isDesktopViewport = isDesktopViewport;

  // Location functionality
  readonly userLocation = this.locationService.userLocation;
  readonly isLocationLoading = this.locationService.isLocationLoading;
  readonly locationError = this.locationService.locationError;
  readonly hasLocation = this.locationService.hasLocation;
  readonly permissionStatus = this.locationService.permissionStatus;

  // Check if filters are active for UI adjustments
  readonly hasActiveFilters = computed(() => {
    return this.filterService.selectedMaterials().length > 0 ||
      this.filterService.showOnlyOpen() ||
      !this.filterService.showContainers() ||
      !this.filterService.showEvents() ||
      !this.filterService.showRoutes() ||
      !this.filterService.showPoints();
  });
  // Expose materialsState for template access
  readonly materialsState = materialsState;
  // Using signals from global state
  readonly selectedMaterials = materialsState.selectedMaterials;
  readonly selectedMaterialValues = computed(() => this.selectedMaterials().map(m => m.value).filter(Boolean) as string[]);
  readonly materialOptions = materialsState.mainMaterials; // Only main materials for dropdown

  private stateEffect = effect(() => {
    this.selectedMaterials();
    // console.log('Selected materials updated:', materials);
    // Reset to first page when materials change
    this.currentPage.set(1);
    // Map markers will update automatically via computed signal
  });

  // Effect to reset page when filters change
  private filterEffect = effect(() => {
    this.filterService.showOnlyOpen();
    this.filterService.showContainers();
    this.filterService.searchTerm();
    // Reset to first page when any filter changes
    this.currentPage.set(1);
  });

  // Effect to handle map recenter requests from global state
  private mapRecenterEffect = effect(() => {
    const recenterRequest = mapEventsState.recenterRequest();
    if (recenterRequest && this.mapComponent) {
      console.log('🗺️ Handling map recenter request from global state:', recenterRequest);
      this.mapComponent.setCenter(
        { lat: recenterRequest.lat, lng: recenterRequest.lng },
        undefined,
        true
      );
      // Clear the request after handling
      mapEventsState.clearRecenterRequest();
    }
  });

  // Effect to handle entity routing - opens modal when entity is found
  private routeEffect = effect(() => {
    const points = this.recyclingPointsSignal();

    // Only process if we have loaded points
    if (points.length === 0) return;

    const entityId = this.route.snapshot.paramMap.get('entityId');
    if (!entityId) return;

    console.log('🔗 Route effect: Looking for entity with ID:', entityId);

    // Search for entity in all categories
    const allEntities = this.recyclingPointsSignal();

    // First try to find by slug, then fallback to id
    let entity = allEntities.find(e => e.slug === entityId);
    if (!entity) {
      console.log('🔗 Route effect: Not found by slug, trying by id...');
      entity = allEntities.find(e => e.id === entityId);
    }

    if (entity) {
      console.log('✅ Route effect: Found entity, opening modal:', entity);

      // Select the marker on the map
      this.selectedPointId = entity.id;
      this.isSelectedFromMapSignal.set(false);

      // Update the map component's selected marker and center on it
      setTimeout(() => {
        if (this.mapComponent) {
          this.mapComponent.updateSelectedMarker(entity.id);
          if (entity.position) {
            this.mapComponent.setCenter(entity.position, 15, true);
          } else if (entity.latitude && entity.longitude) {
            this.mapComponent.setCenter({ lat: entity.latitude, lng: entity.longitude }, 15, true);
          }
        }
      }, 100); // Small delay to ensure map is ready

      // Open appropriate modal based on entity type
      this.openEntityModal(entity, 'list');

      // Clear the route parameter after opening modal to avoid reopening on navigation
      // this.router.navigate(['/home'], { replaceUrl: true });
    } else {
      console.warn('⚠️ Route effect: Entity not found with ID:', entityId);
    }
  });

  recyclingPoints: RecyclingPoint[] = [];
  private selectedPointIdSignal = signal<string | null>(null);
  private isSelectedFromMapSignal = signal<boolean>(false);

  // Computed: returns closest entity ID for the nearby marker layer
  readonly nearbyPointId = computed(() => {
    const stats = this.containerStats();
    return stats.closestEntity?.id || null;
  });

  // Getter for template compatibility - returns explicit selection only
  get selectedPointId(): string | null {
    return this.selectedPointIdSignal();
  }

  // Setter for updating the signal
  set selectedPointId(value: string | null) {
    this.selectedPointIdSignal.set(value);
  }

  // Container popup state
  showContainerPopup = false;
  containerPopupData: RecyclingPoint | null = null;
  containerPopupPosition = { x: 0, y: 0 };

  // Results section animation state
  isResultsSectionAnimating = false;
  isResultsSectionVisible = true;

  // Search section animation state
  isSearchSectionAnimating = false;
  isSearchSectionVisible = true;

  // Global DOM element references
  private searchSection: HTMLElement | null = null;

  // List scroll listener
  // private listScrollListener?: () => void;
  private homeContainerScrollListener?: () => void;
  private resultsScrollListener?: () => void;

  // Convert to signals for better performance
  private recyclingPointsSignal = signal<RecyclingPoint[]>([]);
  // Filter signals now come from FilterService
  // private showOnlyOpenSignal - REMOVED, use filterService.showOnlyOpen()
  // private showContainersSignal - REMOVED, use filterService.showContainers()
  // private searchTermSignal - REMOVED, use filterService.searchTerm()

  // Cache for expensive operations
  private scheduleDaysCache = new Map<string, { code: string; label: string; isOpen: boolean }[]>();
  private scheduleHoursCache = new Map<string, string>();
  private markerColorCache = new Map<string, string>();

  // Helper method to get materials from both API and legacy structures
  private getPointMaterials(point: RecyclingPoint): string[] {
    const pointMaterials: string[] = [];

    // IMPORTANT: Use 'name' as single source of truth for material identification
    // New API structure: materials array with objects
    if (point.materials && Array.isArray(point.materials)) {
      pointMaterials.push(...point.materials.map(m => m.name || '').filter(Boolean));
    }

    // Legacy structure: acceptedMaterials array
    if (point.acceptedMaterials && Array.isArray(point.acceptedMaterials)) {
      pointMaterials.push(...point.acceptedMaterials);
    }

    return pointMaterials;
  }

  private getPointMaterialsAsObjects(point: RecyclingPoint): Material<string>[] {
    const pointMaterials: Material<string>[] = [];

    // IMPORTANT: New API structure: materials array with Material objects
    if (point.materials && Array.isArray(point.materials)) {
      pointMaterials.push(...point.materials);
    }

    // Legacy structure: acceptedMaterials array (strings) - convert to Material objects
    if (point.acceptedMaterials && Array.isArray(point.acceptedMaterials)) {
      const allMaterials = materialsState.availableMaterials();
      point.acceptedMaterials.forEach(materialName => {
        const matchingMaterial = allMaterials.find(m =>
          m.name === materialName || m.value === materialName || m.label === materialName
        );
        if (matchingMaterial) {
          pointMaterials.push(matchingMaterial);
        } else {
          // Create a basic Material object if not found
          pointMaterials.push({
            id: `temp-${materialName}`,
            name: materialName,
            label: materialName,
            value: materialName,
            slug: materialName.toLowerCase()
              .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
          });
        }
      });
    }

    return pointMaterials;
  }

  // Material matching utility - simplified with ID-based comparisons
  private doesPointMatchMaterials(point: RecyclingPoint, selectedMaterials: Material<string>[]): boolean {
    if (selectedMaterials.length === 0) return true;

    const pointMaterials = this.getPointMaterials(point);
    const allAppMaterials = materialsState.availableMaterials();

    return selectedMaterials.some(selectedMaterial => {
      // For each selected material, check if the point accepts it
      // by finding a match between the point's materials and our app material values
      return pointMaterials.some(acceptedMaterial => {
        const normalizeString = (str: string) => str.toLowerCase().trim();

        // Direct match with selected material value
        if (selectedMaterial.value && normalizeString(acceptedMaterial) === normalizeString(selectedMaterial.value)) {
          return true;
        }

        // Check if any app material with the same ID as selected material matches the point material
        const appMaterial = allAppMaterials.find(m => m.id === selectedMaterial.id);
        if (appMaterial && (
          (appMaterial.value && normalizeString(acceptedMaterial) === normalizeString(appMaterial.value)) ||
          normalizeString(acceptedMaterial) === normalizeString(appMaterial.label)
        )) {
          return true;
        }

        return false;
      });
    });
  }

  // Search matching utility - extracted to avoid repetition

  // Utility to remove accents/diacritics for accent-insensitive search
  private normalizeString(str: string): string {
    return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  }

  private doesPointMatchSearch(point: RecyclingPoint, searchTerm: string): boolean {
    if (!searchTerm) return true;

    const normalizedSearch = this.normalizeString(searchTerm);

    // Get address as string
    const addressString = typeof point.address === 'string'
      ? point.address
      : `${point.address?.fullAddress || point.address?.street || ''} ${point.address?.city || ''}`;

    const pointMaterials = this.getPointMaterials(point);

    const searchableText = [
      point.name || '',
      point.type || '',
      point.description || '',
      addressString,
      ...pointMaterials
    ].join(' ');
    const normalizedText = this.normalizeString(searchableText);

    return normalizedText.includes(normalizedSearch);
  }

  // Central unified filtering system that categorizes all entities
  filteredEntities = computed(() => {
    const allPoints = this.recyclingPointsSignal();
    const selectedMaterials = this.selectedMaterials();
    const showOnlyOpen = this.filterService.showOnlyOpen();
    const showContainers = this.filterService.showContainers();
    const showEvents = this.filterService.showEvents();
    const showRoutes = this.filterService.showRoutes();
    const showPoints = this.filterService.showPoints();
    const searchTerm = this.filterService.searchTerm().toLowerCase().trim();

    console.log('🔄 filteredEntities computation started with:', {
      allPoints: allPoints.length,
      selectedMaterials,
      showOnlyOpen,
      showContainers,
      showEvents,
      showRoutes,
      showPoints,
      searchTerm
    });

    // Initialize result categories
    const result = {
      recyclingPoints: [] as RecyclingPoint[],
      containers: [] as RecyclingPoint[],
      routePoints: [] as RecyclingPoint[],
      mapMarkers: [] as any[]
    };

    // First pass: categorize all points by entity type and apply basic filters
    allPoints.forEach(point => {
      const pointType = point.type || 'point';

      // Apply search filter first (applies to all entity types including containers)
      if (!this.doesPointMatchSearch(point, searchTerm)) {
        return; // Skip if doesn't match search
      }

      // Categorize by point type and apply specific filters
      switch (pointType) {
        case 'container':
          // Container-specific filtering
          if (showContainers) {
            // Containers show regardless of material filter if showContainers is true
            // But still apply material filter if materials are selected
            if (selectedMaterials.length === 0 || this.doesPointMatchMaterials(point, selectedMaterials)) {
              result.containers.push(point);
            }
          }
          break;

        case 'route':
        case 'area':
          // Schedule/route entities filtering - apply showRoutes filter
          if (showRoutes && this.doesPointMatchMaterials(point, selectedMaterials)) {
            result.routePoints.push(point);
          }
          break;

        case 'event':
          // Event filtering - apply showEvents filter
          if (showEvents && this.doesPointMatchMaterials(point, selectedMaterials)) {
            // Apply open/closed filter to events too
            if (!showOnlyOpen || point.isCurrentlyOpen === true) {
              result.recyclingPoints.push(point);
            }
          }
          break;

        default:
          // Regular recycling points filtering (point, mobile_unit, etc.) - apply showPoints filter
          if (showPoints && this.doesPointMatchMaterials(point, selectedMaterials)) {
            // Apply open/closed filter to recycling points only
            if (!showOnlyOpen || point.isCurrentlyOpen === true) {
              result.recyclingPoints.push(point);
            }
          }
          break;
      }
    });

    // Generate map markers from all filtered entities
    const allFilteredPoints = [
      ...result.recyclingPoints,
      ...result.containers,
      ...result.routePoints
    ];

    result.mapMarkers = allFilteredPoints
      .filter(point => {
        // Check for coordinates from API or legacy position field
        const hasPosition = point.position || (point.latitude && point.longitude);
        const pointType = point.type || 'point';
        const hasPolygonData = (pointType === 'route' || pointType === 'area') &&
          (point.polygon?.coordinates?.length > 0 || point.raw?.polygon_data?.coordinates?.length > 0);

        return hasPosition || hasPolygonData;
      })
      .map((point) => {
        const pointType = point.type || 'point';

        // Get position from API data (latitude/longitude) or legacy position field
        let position = point.position;

        // Primary: use API latitude/longitude directly
        if (!position && point.latitude && point.longitude) {
          position = {
            lat: point.latitude,
            lng: point.longitude
          };
        }

        // Fallback for polygon points
        if (!position && (pointType === 'route' || pointType === 'area') && point.raw?.coordinates) {
          position = {
            lat: point.raw.coordinates.latitude,
            lng: point.raw.coordinates.longitude
          };
        }

        // Create marker object
        const marker: any = {
          id: point.id,
          position: position || { lat: 0, lng: 0 }, // Final fallback
          title: point.name,
          description: typeof point.address === 'string' ? point.address : point.address?.fullAddress || point.address?.street || '',
          color: this.getMarkerColor(pointType),
          data: point
        };

        // Add custom icon for containers
        if (pointType === 'container') {
          marker.imageUrl = '/assets/icons/container.png';
          marker.imageSize = { width: 14, height: 'auto' };
        }
        return marker;
      });

    console.log('🔄 filteredEntities computed result:', {
      recyclingPoints: result.recyclingPoints.length,
      containers: result.containers.length,
      routePointsLenght: result.routePoints.length,
      routePoints: result.routePoints,
      mapMarkers: result.mapMarkers.length
    });

    return result;
  });

  // Computed filtered points using signals for better performance
  // Shows ALL entity types in a single unified list (no type filtering)
  filteredAndSortedRecyclingPoints = computed(() => {
    const filteredData = this.filteredEntities();
    // Combine ALL filtered entities into a single array - no type separation
    const allFilteredPoints = [
      ...filteredData.recyclingPoints,
      ...filteredData.containers,
      ...filteredData.routePoints
    ];
    console.log("filteredPoints (all types unified)", allFilteredPoints);
    return this.addSelectedPointToTop(allFilteredPoints);
  });

  // Computed property for schedule/polygon entities
  routePoints = computed(() => {
    const filteredData = this.filteredEntities();
    return filteredData.routePoints;
  });

  // Computed property for filtered map markers with optimization
  filteredMapMarkers = computed(() => {
    const filteredData = this.filteredEntities();

    console.log('🗺️ filteredMapMarkers using unified filtering:', {
      totalMarkers: filteredData.mapMarkers.length,
      markers: filteredData.mapMarkers.map(m => ({
        id: m.id,
        title: m.title,
        type: m.data?.type
      }))
    });

    return filteredData.mapMarkers;
  });

  // Container statistics computed properties
  containerStats = computed(() => {
    const filteredData = this.filteredEntities();
    const containers = filteredData.containers;

    const totalContainers = containers.length;

    // Count containers that accept specific materials
    const materialCounts: { [key: string]: number } = {};
    const materialContainers: { [key: string]: RecyclingPoint[] } = {};

    containers.forEach(container => {
      this.getPointMaterials(container).forEach(material => {
        const normalizedMaterial = material.toLowerCase().trim();
        if (!materialCounts[normalizedMaterial]) {
          materialCounts[normalizedMaterial] = 0;
          materialContainers[normalizedMaterial] = [];
        }
        materialCounts[normalizedMaterial]++;
        materialContainers[normalizedMaterial].push(container);
      });
    });

    // Find closest entity from ALL filtered entities (already filtered by materials, search, etc.)
    let closestDistance = '';
    let closestEntity: RecyclingPoint | null = null;
    const userLoc = this.userLocation();

    // Combine all already-filtered entities
    const allFilteredEntities: RecyclingPoint[] = [
      ...filteredData.recyclingPoints,
      ...filteredData.containers,
      ...filteredData.routePoints
    ];

    if (userLoc && allFilteredEntities.length > 0) {
      const closestResult = this.locationService.findClosestEntity(allFilteredEntities);
      if (closestResult) {
        closestDistance = this.locationService.formatDistance(closestResult.distance);
        closestEntity = closestResult.entity;
      }
    }

    // Priority materials to show (in order of preference)
    const priorityMaterials = ['plástico', 'metal', 'vidrio', 'papel', 'cartón'];

    // Get top materials, prioritizing the ones we want to show
    const availableMaterials = Object.keys(materialCounts);
    let topMaterials = priorityMaterials
      .filter(material => availableMaterials.some(available =>
        available.includes(material) || material.includes(available)
      ))
      .slice(0, 2)
      .map(material => {
        // Find the actual material name that matches
        const actualMaterial = availableMaterials.find(available =>
          available.includes(material) || material.includes(available)
        ) || material;
        return {
          material,
          count: materialCounts[actualMaterial] || 0
        };
      });

    // If we don't have enough priority materials, fall back to most common ones
    if (topMaterials.length < 2) {
      const fallbackMaterials = Object.entries(materialCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 2 - topMaterials.length)
        .map(([material, count]) => ({ material, count }));

      topMaterials = [...topMaterials, ...fallbackMaterials];
    }

    return {
      totalContainers,
      materialCounts,
      materialContainers,
      closestDistance,
      closestEntity,
      topMaterials
    };
  });

  // Computed property for filtered recycling points count by type
  filteredRecyclingPointsStats = computed(() => {
    const filteredData = this.filteredEntities();
    const recyclingPoints = filteredData.recyclingPoints;
    const containers = filteredData.containers;
    const routePoints = filteredData.routePoints;

    // Count by type
    const typeCounts: Record<string, number> = {};

    // Count recycling points by type
    recyclingPoints.forEach(point => {
      const pointType = point.type || 'point';
      typeCounts[pointType] = (typeCounts[pointType] || 0) + 1;
    });

    // Add containers count
    typeCounts['container'] = containers.length;

    // Add routes/areas from schedule entities
    routePoints.forEach(entity => {
      const pointType = entity.type || 'route';
      typeCounts[pointType] = (typeCounts[pointType] || 0) + 1;
    });

    const totalPoints = recyclingPoints.length + containers.length + routePoints.length;

    return {
      totalPoints,
      typeCounts,
      pointsCount: recyclingPoints.length,
      containersCount: containers.length,
      routesCount: typeCounts['route'] || 0,
      areasCount: typeCounts['area'] || 0
    };
  });

  // Type counts filtered by current map viewport
  viewportTypeCounts = computed((): Record<string, number> => {
    const bounds = this.mapBounds();
    const markers = this.filteredMapMarkers();
    const typeCounts: Record<string, number> = {};

    if (!bounds) {
      // No bounds yet (map not loaded) — fall back to full counts
      return this.filteredRecyclingPointsStats().typeCounts;
    }

    for (const marker of markers) {
      const pos = marker.position;
      if (!pos?.lat || !pos?.lng) continue;
      if (pos.lat < bounds.south || pos.lat > bounds.north) continue;
      if (pos.lng < bounds.west || pos.lng > bounds.east) continue;

      const pointType = marker.data?.type || 'point';
      typeCounts[pointType] = (typeCounts[pointType] || 0) + 1;
    }

    return typeCounts;
  });

  // Computed signal for results summary component
  // Always shows all types - inactive ones are displayed with reduced opacity but show total count
  resultsSummaryTypeCounts = computed((): TypeCount[] => {
    const stats = this.filteredRecyclingPointsStats();
    const allPoints = this.recyclingPointsSignal();
    const typeCounts: TypeCount[] = [];

    // Calculate total counts from all points (unfiltered by type)
    let totalPointsCount = 0;
    let totalContainersCount = 0;
    let totalRoutesCount = 0;

    allPoints.forEach(point => {
      const pointType = point.type || 'point';
      switch (pointType) {
        case 'container':
          totalContainersCount++;
          break;
        case 'route':
        case 'area':
          totalRoutesCount++;
          break;
        case 'event':
          // Events shown with points for now
          totalPointsCount++;
          break;
        default:
          totalPointsCount++;
          break;
      }
    });

    // Always add points count (show total when inactive, filtered when active)
    const showPoints = this.filterService.showPoints();
    const pointsCount = showPoints ? stats.pointsCount : totalPointsCount;
    typeCounts.push({
      type: 'place',
      count: pointsCount,
      label: pointsCount === 1 ? this.t.instant('home.points') : this.t.instant('home.pointsPlural'),
      active: showPoints
    });

    // Always add routes/areas count (show total when inactive, filtered when active)
    const showRoutes = this.filterService.showRoutes();
    const filteredRoutes = (stats.routesCount || 0) + (stats.areasCount || 0);
    const routesCount = showRoutes ? filteredRoutes : totalRoutesCount;
    typeCounts.push({
      type: 'route',
      count: routesCount,
      label: routesCount === 1 ? this.t.instant('home.routeSingular') : this.t.instant('home.routePlural'),
      active: showRoutes
    });

    // Always add containers count (show total when inactive, filtered when active)
    const showContainers = this.filterService.showContainers();
    const containersCount = showContainers ? stats.containersCount : totalContainersCount;
    typeCounts.push({
      type: 'container',
      count: containersCount,
      label: containersCount === 1 ? this.t.instant('home.containerSingular') : this.t.instant('home.containerPlural'),
      active: showContainers
    });

    return typeCounts;
  });

  // Handle type badge click to toggle filters
  onTypeCountClick(type: 'place' | 'container' | 'event' | 'route'): void {
    switch (type) {
      case 'place':
        this.filterService.setShowPoints(!this.filterService.showPoints());
        break;
      case 'container':
        this.filterService.setShowContainers(!this.filterService.showContainers());
        break;
      case 'event':
        this.filterService.setShowEvents(!this.filterService.showEvents());
        break;
      case 'route':
        this.filterService.setShowRoutes(!this.filterService.showRoutes());
        break;
    }
  }

  // Handle nearby result click - select marker and open the closest point/container
  onNearbyResultClick(): void {
    const stats = this.containerStats();
    if (stats.closestEntity) {
      const entity = stats.closestEntity;
      this.posthog.capture('home_nearby_result_click', {
        pointId: entity.id,
        pointName: entity.name,
        distance: stats.closestDistance,
        entityType: entity.type
      });

      // Select marker on map (unified for all entity types)
      this.selectedPointId = entity.id;
      this.isSelectedFromMapSignal.set(false);
      if (this.mapComponent) {
        this.mapComponent.updateSelectedMarker(entity.id);
        if (entity.position) {
          this.mapComponent.setCenter(entity.position, undefined, true);
        }
      }
      this.cdr.detectChanges();

      // Open appropriate modal based on entity type
      this.openEntityModal(entity, 'map');
    }
  }

  // Computed signal for distance in meters to nearest point
  resultsSummaryDistanceMeters = computed(() => {
    const stats = this.containerStats();
    if (stats.closestDistance) {
      // Parse distance string like "220m" or "1.2km" to meters
      const distance = stats.closestDistance;
      if (distance.includes('km')) {
        return parseFloat(distance) * 1000;
      }
      return parseFloat(distance);
    }
    return 0;
  });

  // Computed signal for materials at nearest point
  // Returns only the SELECTED materials that the closest entity has
  resultsSummaryMaterials = computed(() => {
    const stats = this.containerStats();
    const closestEntity = stats.closestEntity;
    const selectedMaterials = this.filterService.selectedMaterials();

    // Only show if there are selected materials and a closest entity
    if (!closestEntity || selectedMaterials.length === 0) {
      return [];
    }

    // Get materials from the closest entity
    const entityMaterials = this.getPointMaterials(closestEntity);
    const normalizedEntityMaterials = entityMaterials.map(m => m.toLowerCase().trim());

    // Get all available materials to match IDs to full objects
    const allMaterials = materialsState.availableMaterials();

    // Filter to only selected materials that exist in the closest entity
    return selectedMaterials
      .map(selectedId => {
        // Find the material object by ID
        const material = allMaterials.find(m => m.id === selectedId);
        if (!material) return null;

        // Check if this material exists in the entity
        const materialName = (material.label || material.value || '').toLowerCase().trim();
        const isInEntity = normalizedEntityMaterials.some(em =>
          em.includes(materialName) || materialName.includes(em)
        );

        return isInEntity ? material : null;
      })
      .filter((m): m is Material<string> => m !== null);
  });

  // Return container popup materials as Material[]
  containerPopupReorderedMaterials = computed(() => {
    if (!this.containerPopupData) return [];

    const point = this.containerPopupData;

    if (point.materials && Array.isArray(point.materials) && point.materials.length > 0) {
      const allMaterials = materialsState.availableMaterials();
      return point.materials.map(m => {
        const slug = m.slug || allMaterials.find(am => am.id === m.id)?.slug;
        return {
          id: m.id,
          name: m.name,
          label: m.name || m.label || '',
          slug,
          selected: false
        };
      });
    }

    return [];
  });

  // Helper method for template to get points summary by type
  getPointsSummaryText(): string {
    const stats = this.filteredRecyclingPointsStats();
    const parts: string[] = [];

    // Add points count (excluding containers and routes)
    if (stats.pointsCount > 0) {
      parts.push(`${stats.pointsCount} ${this.t.instant('home.pointsPlural')}`);
    }

    // Add routes count
    if (stats.routesCount > 0) {
      parts.push(`${stats.routesCount} ${stats.routesCount === 1 ? this.t.instant('home.routeSingular') : this.t.instant('home.routePlural')}`);
    }

    // Add areas count
    if (stats.areasCount > 0) {
      parts.push(`${stats.areasCount} ${stats.areasCount === 1 ? this.t.instant('home.areaSingular') : this.t.instant('home.areaPlural')}`);
    }

    // Add containers count
    if (stats.containersCount > 0) {
      parts.push(`${stats.containersCount} ${stats.containersCount === 1 ? this.t.instant('home.containerSingular') : this.t.instant('home.containerPlural')}`);
    }

    if (parts.length === 0) {
      return this.t.instant('home.zeroPoints');
    }

    return parts.join(', ');
  }

  // Helper method for template to get top materials as string
  getTopMaterialsText(): string {
    try {
      const stats = this.containerStats();
      if (!stats || !stats.topMaterials || stats.topMaterials.length === 0) {
        return this.t.instant('home.materialsAvailable');
      }
      return stats.topMaterials.map(m => m.material).join(', ');
    } catch (error) {
      console.error('Error getting top materials text:', error);
      return this.t.instant('home.materialsAvailable');
    }
  }

  // Location-related methods
  async requestLocation(): Promise<void> {
    this.posthog.capture('home_request_location', {
      source: 'button'
    });
    try {
      const location = await this.locationService.requestLocation();
      console.log('Location obtained successfully:', location);

      // Load nearby points from user's current location
      if (location) {
        this.loadNearbyPoints(location.lat, location.lng);
      }
    } catch (error) {
      console.error('Failed to get location:', error);
    }
  }

  async openLocationInputModal(): Promise<void> {
    this.posthog.capture('home_open_location_input', {
      source: 'button'
    });
    const { LocationInputComponent } = await import('../shared/location-input/location-input.component');

    const popover = await this.customPopoverService.create({
      component: LocationInputComponent,
      cssClass: 'location-input-popover',
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.LocationSearch,
      componentProps: {
        isModal: true,
        initialAddress: '',
        userLocation: this.userLocation(),
        showMapOption: true,
        showCurrentLocationOption: true
      },
      showBackdrop: true,
      backdropDismiss: true
    });

    popover.onDidDismiss().then((result) => {
      if (result.data?.location) {
        const location = result.data.location;
        // Update user location with the selected location
        this.locationService.setUserLocation({
          lat: location.lat,
          lng: location.lng,
          timestamp: Date.now()
        });
        console.log('Location selected from popover:', location);
      }
    });
  }

  getCurrentLocationName(): string {
    const userLoc = this.userLocation();
    if (!userLoc) return 'Rosario';

    return this.locationService.getApproximateLocationName(userLoc.lat, userLoc.lng);
  }

  // Sort recycling points by distance from user location
  getSortedPointsByDistance(): RecyclingPoint[] {
    const points = this.filteredAndSortedRecyclingPoints();
    const userLoc = this.userLocation();

    if (!userLoc) return points;

    return this.locationService.sortByDistance(points);
  }

  // Handle pin placement from map
  onPinPlaced(position: { lat: number; lng: number }): void {
    // Set the user location to the placed pin position
    this.locationService.setUserLocation({
      lat: position.lat,
      lng: position.lng,
      timestamp: Date.now()
    });

    console.log('Pin placed at:', position);
    console.log('Location set to:', this.getCurrentLocationName());
  }

  // Activate pin mode on the map
  activatePinMode(): void {
    this.posthog.capture('home_activate_pin_mode', {
      source: 'button'
    });
    if (this.mapComponent) {
      this.mapComponent.togglePinMode();
    }
  }

  // Handle pin mode changes from the map component
  onPinModeChange(isPinModeActive: boolean): void {
    console.log("isPinModeActive", isPinModeActive);
    this.recyclingFiltersComponent.closeLocationComponent()
    this.isPinModeActive = isPinModeActive;

    if (isPinModeActive) {
      // Entering pin mode: hide results section and collapse filters
      this.animateResultsSectionOut();

      // Store current filter state and collapse if expanded
      this.originalFiltersExpanded = this.isFiltersExpanded;
      this.recyclingFiltersComponent.closeFilter();
    } else {
      // Exiting pin mode: restore UI
      this.animateResultsSectionIn();
      this.recyclingFiltersComponent.toggleFilters();
      // Restore original filter state
      if (this.originalFiltersExpanded) {
        this.isFiltersExpanded = true;
      }
    }
  }

  // Helper method to get address as display string
  getAddressString(point: RecyclingPoint): string {
    if (!point.address) return '';

    // Handle object address structure
    const parts = [];
    const addressStr = point.address.fullAddress || point.address.street;
    if (addressStr) {
      parts.push(addressStr);
    }
    if (point.address.city !== 'Rosario') {
      parts.push(point.address.city);
    }

    return parts.join(', ') || point.address.city || '';
  }

  // Convert RecyclingPoint to RouteEntity for route-card component
  convertToRouteEntity(point: RecyclingPoint): RouteEntity {
    return {
      id: point.id,
      slug: point.slug,
      name: point.name,
      address: this.getAddressString(point),
      acceptedMaterials: this.getPointMaterialsAsObjects(point),
      description: point.description,
      polygon: point.polygon || point.raw?.polygon,
      referenceLinks: point.referenceLinks,
      raw: point.raw || point
    };
  }

  // Get distance to a recycling point from user location
  getDistanceToPoint(point: RecyclingPoint): string | null {
    const userLoc = this.userLocation();
    if (!userLoc || !point.position) return null;

    const distance = this.locationService.calculateDistanceFromUser(point.position.lat, point.position.lng);
    return distance ? this.locationService.formatDistance(distance) : null;
  }

  /**
   * Get point type for conditional rendering
   * Returns: 'container' | 'event' | 'point' (default for places)
   */
  getPointType(point: RecyclingPoint): string {
    return point.type || 'point';
  }

  /**
   * Check if point is a container type
   */
  isContainerType(point: RecyclingPoint): boolean {
    return point.type === 'container';
  }

  /**
   * Check if point is an event type
   */
  isEventType(point: RecyclingPoint): boolean {
    return point.type === 'event';
  }

  /**
   * Check if point is a route/area type
   */
  isRouteType(point: RecyclingPoint): boolean {
    return point.type === 'route' || point.type === 'area';
  }

  /**
   * Open the appropriate modal based on entity type.
   * Single source of truth for entity→modal routing.
   */
  openEntityModal(point: RecyclingPoint, triggeredBy: 'map' | 'list'): void {
    // Force close filters when opening any modal
    if (this.recyclingFiltersComponent) {
      this.recyclingFiltersComponent.closeFilter();
    }

    switch (point.type) {
      case 'container':
        this.openContainerModal(point, triggeredBy);
        break;
      case 'route':
      case 'area':
        this.openRouteModal(point, triggeredBy);
        break;
      case 'event':
        this.openEventModal(point, triggeredBy);
        break;
      default:
        this.openPlaceModal(point, triggeredBy);
        break;
    }
  }

  /**
   * Trigger vibration feedback
   */
  private triggerVibration(duration: number = 50): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(duration);
    }
  }

  ngOnInit() {
    // Skip browser-specific code during prerender
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Register this component instance globally for tab navigation
    if (this.platform.window) {
      (this.platform.window as any).homeComponentInstance = this;
    }

    if (this.platform.isBrowser && this.platform.window) {
      const originalMatchMedia = this.platform.window.matchMedia.bind(this.platform.window);
      console.log("Overriding window.matchMedia to avoid errors in certain environments", originalMatchMedia);

      this.platform.window.matchMedia = (query: string) => {
        if (query === '(prefers-reduced-motion: reduce)') {
          return {
            matches: false,
            media: query,
            onchange: null,
            addListener: () => { },
            removeListener: () => { },
            addEventListener: () => { },
            removeEventListener: () => { },
            dispatchEvent: () => true,
          } as MediaQueryList;
        }
        return originalMatchMedia(query);
      };
    }
    console.log("ngOnInit");
    // Load global reference data from API (source of truth)
    this.loadGlobalReferenceData();

    // Only access DOM in browser environment
    // Load recycling points data
    this.loadRecyclingPoints();

    // Start map point monitoring
    if (isPlatformBrowser(this.platformId)) {
      this.startMapPointMonitoring();
    }

    // Show TyC popup if user hasn't accepted yet
    if (isPlatformBrowser(this.platformId) && !TycComponent.hasAcceptedTerms()) {
      setTimeout(() => {
        this.showWelcomeTycPopup();
      }, 1000);
    }
  }

  /**
   * Show the Terms and Conditions welcome popup with custom animation
   */
  private async showWelcomeTycPopup(): Promise<void> {
    const popover = await this.customPopoverService.create({
      component: TycComponent,
      cssClass: 'tyc-popover',
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.Terms,
      backdropDismiss: false,
      // Custom animation options
      animationDuration: 180,
      scaleFrom: 0.8,
      scaleTo: 1,
      opacityFrom: 0.5,
      opacityTo: 1
    });

    const { data, role } = await popover.onDidDismiss();

    if (role === 'accepted' && data?.accepted) {
      console.log('TyC accepted:', data);
    }
  }

  ngAfterViewInit() {
    console.log('🚀 ngAfterViewInit started');
    if (isPlatformBrowser(this.platformId)) {
      history.pushState({ type: 'secure' }, '');
    }

    // Initialize DOM element references
    this.initializeDOMReferences();
    // Trigger entrance animations with a slight delay for smoother experience
    this.triggerEntranceAnimations();

    // Set map component reference in custom modal controller after view init
    /*     if (this.mapComponent) {
          this.customModalCtrl.setMapComponent(this.mapComponent);
        } 
        console.log('✅ Map component reference set in custom modal controller');
      } else {
        console.log('⚠️ Map component not available in ngAfterViewInit');
    }
    */

    // Setup scroll listener for list container
    console.log('🎯 About to setup list scroll listener...');
    console.log('📋 listScrollContainer availability:', !!this.listScrollContainer);
    console.log('🏠 homeContainer availability:', !!this.wrapperListScroll);
    if (this.listScrollContainer) {
      console.log('📋 listScrollContainer element:', this.listScrollContainer.nativeElement);
    }
    if (this.wrapperListScroll) {
      console.log('🏠 homeContainer element:', this.wrapperListScroll.nativeElement);
    }

    this.setupListScrollListener();

    // Initialize results section position
    this.initializeResultsPosition();

    console.log('✅ ngAfterViewInit completed');
  }

  ngOnDestroy() {
    // Unregister this component instance
    if (isPlatformBrowser(this.platformId) && (window as any).homeComponentInstance === this) {
      delete (window as any).homeComponentInstance;
    }

    // Clear map point monitoring
    if (this.checkMapPointInterval) {
      clearInterval(this.checkMapPointInterval);
    }
    if (this.mapBoundsDebounceTimer) {
      clearTimeout(this.mapBoundsDebounceTimer);
    }

    // Clean up the effects when component is destroyed
    this.stateEffect.destroy();
    this.filterEffect.destroy();
    this.routeEffect.destroy();

    // Clean up scroll listener
    this.removeListScrollListener();

    // Clear scroll timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.listScrollTimeout) {
      clearTimeout(this.listScrollTimeout);
    }
  }

  private setupListScrollListener() {
    console.log('🔧 Setting up list scroll listener');

    if (!isPlatformBrowser(this.platformId)) {
      console.log('❌ Not in browser platform, skipping scroll listener setup');
      return;
    }

    if (!this.listScrollContainer || !this.wrapperListScroll || !this.resultsSection) {
      console.log('❌ Required containers not available, skipping scroll listener setup');
      return;
    }

    console.log('✅ Browser platform detected, setting up scroll listeners');

    /*     this.listScrollListener = () => {
          // Throttle list scroll logging to avoid overhead
          if (this.listScrollTimeout) {
            clearTimeout(this.listScrollTimeout);
          }
          
          this.listScrollTimeout = setTimeout(() => {
            console.log('📜 List scroll event triggered → calling onListScroll()');
            this.onListScroll();
          }, 100); // Only log/process after 100ms of no scrolling
        }; */

    this.homeContainerScrollListener = () => {
      // Only update signals, don't call expensive checks during scroll
      const homeContainerScrollTop = this.wrapperListScroll?.nativeElement?.scrollTop || 0;
      this.wrapperScrollTop.set(homeContainerScrollTop);

      // Clear existing timeout and set new one - only check when scrolling stops
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        console.log('🏠 User released scroll, running behavior check at:', {
          scrollTop: homeContainerScrollTop.toFixed(2)
        });
        this.checkScrollBehavior();
      }, 150); // Only run expensive checks after 150ms of no scrolling
    };

    // Wrapper scroll listener for results section position management
    this.resultsScrollListener = () => {
      this.onWrapperScroll();
    };

    // this.listScrollContainer.nativeElement.addEventListener('scroll', this.listScrollListener, { passive: true });
    this.wrapperListScroll.nativeElement.addEventListener('scroll', this.homeContainerScrollListener, { passive: true });
    this.wrapperListScroll.nativeElement.addEventListener('scroll', this.resultsScrollListener, { passive: true });

    console.log('🎧 Event listeners attached (list scroll + home container scroll + wrapper scroll for positioning)');

    // Initial check
    console.log('🎯 🚀 PERFORMING INITIAL SCROLL BEHAVIOR CHECK...');
    console.log('🎯 Trigger: Component initialization (ngAfterViewInit)');
    this.checkScrollBehavior();

    // 🧪 DEV: Expose component to global scope for testing
    if (this.platform.isBrowser && this.isProgrammingMode() && this.platform.window) {
      (this.platform.window as any)['homeComponent'] = this;
      console.log('🧪 DEV MODE: Component exposed as window.homeComponent');
      console.log('🧪 Try calling: homeComponent.testScrollBehavior()');
    }
  }

  private removeListScrollListener() {
    console.log('🗑️ Cleaning up scroll listeners');

    /*     if (this.listScrollListener && this.listScrollContainer) {
          this.listScrollContainer.nativeElement.removeEventListener('scroll', this.listScrollListener);
          this.listScrollListener = undefined;
          console.log('✅ List scroll listener removed');
        } else {
          console.log('⚠️ No list scroll listener to remove');
        }
     */
    if (this.homeContainerScrollListener) {
      this.wrapperListScroll.nativeElement.removeEventListener('scroll', this.homeContainerScrollListener);
      this.homeContainerScrollListener = undefined;
      console.log('✅ Home container scroll listener removed');
    } else {
      console.log('⚠️ No home container scroll listener to remove');
    }

    if (this.resultsScrollListener && this.wrapperListScroll) {
      this.wrapperListScroll.nativeElement.removeEventListener('scroll', this.resultsScrollListener);
      this.resultsScrollListener = undefined;
      console.log('✅ Wrapper scroll listener removed');
    } else {
      console.log('⚠️ No wrapper scroll listener to remove');
    }
  }

  private checkScrollBehavior() {
    if (!this.listScrollContainer || !this.wrapperListScroll) {
      console.log('❌ checkScrollBehavior: listScrollContainer or homeContainer not available');
      return;
    }

    const listElement = this.listScrollContainer.nativeElement;
    const homeElement = this.wrapperListScroll.nativeElement;

    // Get all element dimensions and positions
    const listRect = listElement.getBoundingClientRect();
    const homeRect = homeElement.getBoundingClientRect();

    // Calculate offsetTop relative to home container
    const offsetTopFromHome = listRect.top - homeRect.top;

    console.log('📏 🔍 COMPREHENSIVE ELEMENT ANALYSIS:');

    // 📐 CRITICAL MEASUREMENTS
    const minOffsetForScrolling = window.innerHeight - 52;
    const shouldAllowInnerScrolling = offsetTopFromHome <= minOffsetForScrolling;

    // 🔄 STATE TRANSITION LOGIC
    if (shouldAllowInnerScrolling) {
      if (listElement.style.overflowY !== 'auto') {
        listElement.style.overflowY = 'auto';
        /*         console.log('✅ 🔄 SCROLL ENABLED:', {
                  reason: `offsetTopFromHome (${offsetTopFromHome.toFixed(2)}px) ≤ threshold (${minOffsetForScrolling}px)`,
                  stateChange: `${previousOverflowY} → auto`,
                  effect: 'List can now be scrolled internally'
                }); */
      } else {
        listElement.style.overflowY = 'hidden';
        /*         console.log('✅ ⚡ SCROLL ALREADY ENABLED:', {
                  reason: `offsetTopFromHome (${offsetTopFromHome.toFixed(2)}px) ≤ threshold (${minOffsetForScrolling}px)`,
                  currentState: listElement.style.overflowY,
                  effect: 'No change needed'
                }); */
      }
    } else {
      if (listElement.style.overflowY !== 'hidden') {
        listElement.style.overflowY = 'hidden';
        // Only reset scroll position if we're at the bottom position (map view)
        // This prevents unwanted scroll resets when toggling to list view
        if (this.currentViewPositionSignal() === 'bottom') {
          listElement.scrollTop = 0;
        }
        /*         console.log('🚫 🔄 SCROLL DISABLED:', {
                  reason: `offsetTopFromHome (${offsetTopFromHome.toFixed(2)}px) > threshold (${minOffsetForScrolling}px)`,
                  stateChange: `${previousOverflowY} → hidden`,
                  effect: 'List scroll disabled, scroll position reset to top'
                }); */
      } else {
        console.log('🚫 ⚡ SCROLL ALREADY DISABLED:', {
          reason: `offsetTopFromHome (${offsetTopFromHome.toFixed(2)}px) > threshold (${minOffsetForScrolling}px)`,
          currentState: listElement.style.overflowY,
          effect: 'No change needed'
        });
      }
    }

    // 📊 FINAL STATE SUMMARY
    const finalState = {
      canScroll: listElement.style.overflowY === 'auto' && listElement.scrollHeight > listElement.clientHeight,
      overflowY: listElement.style.overflowY,
      hasScrollableContent: listElement.scrollHeight > listElement.clientHeight,
      scrollPosition: listElement.scrollTop,
      willTriggerScrollEvents: listElement.style.overflowY === 'auto'
    };

    console.log('� FINAL STATE SUMMARY:', finalState);
    console.log('═══════════════════════════════════════════════════════');
  }

  // 🧪 TEST METHOD: Call this from browser console to manually test scroll behavior
  public testScrollBehavior() {
    console.log('🧪 ═══════════ MANUAL SCROLL BEHAVIOR TEST ═══════════');
    console.log('🧪 🎯 TRIGGER: Manual test method called from console');
    console.log('🧪 Triggering checkScrollBehavior manually...');
    this.checkScrollBehavior();

    console.log('🧪 📊 CONTEXT INFORMATION:');
    console.log('🧪 Current home container scroll position:', (this.wrapperListScroll?.nativeElement?.scrollTop || 0).toFixed(2) + 'px');
    console.log('🧪 TESTING SUGGESTIONS:');
    console.log('🧪 • Scroll the home container up/down to see if scroll behavior changes');
    console.log('🧪 • Try expanding/collapsing schedule entities to see layout changes');
    console.log('🧪 • Look for "🎯 HOME CONTAINER SCROLL EVENT TRIGGERED" in logs when scrolling');
    console.log('🧪 ═══════════════════ END TEST ═══════════════════');
  }

  // Handle wrapper scroll for smooth snapping behavior
  private scrollTimeout: any;
  private listScrollTimeout: any;

  private onWrapperScroll() {
    if (!this.wrapperListScroll?.nativeElement) {
      return;
    }

    const scrollElement = this.wrapperListScroll.nativeElement;
    const scrollTop = scrollElement.scrollTop;

    // Clear existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Set timeout to detect when scrolling stops
    this.scrollTimeout = setTimeout(() => {
      this.snapToNearestPosition(scrollTop);
    }, 150); // Wait 150ms after scroll stops

    // console.log('📊 Wrapper scroll detected:', {
    //   scrollTop: scrollTop.toFixed(2),
    //   direction: scrollTop > this.lastWrapperScrollTop ? 'down' : 'up'
    // });
    this.wrapperScrollTop.set(scrollTop);
  }

  // Calculate dynamic top position based on viewport and element heights
  // retryCount is used internally to limit retries when .map-placement is not yet in the DOM
  private calculateTopPosition(retryCount: number = 0): number {
    if (!isPlatformBrowser(this.platformId)) return 460; // SSR fallback, no window available

    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 100;

    // Get search controls height (top-pane)
    // const topPaneElement = this.platform.querySelector<HTMLElement>('.top-pane');
    // const searchControlsHeight = topPaneElement?.offsetHeight || 150; // fallback
    const mapPlacementElement = this.platform.querySelector<HTMLElement>('.map-placement');
    console.log("mapPlacementElement", mapPlacementElement);

    // If .map-placement not found, schedule a retry to re-snap once it appears
    if (!mapPlacementElement && retryCount < MAX_RETRIES) {
      console.warn(`⏳ .map-placement not found (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS}ms...`);
      setTimeout(() => {
        const retried = this.calculateTopPosition(retryCount + 1);
        // Re-snap with the correct value once the element is available
        if (mapPlacementElement || retryCount + 1 >= MAX_RETRIES) return;
        if (this.wrapperListScroll?.nativeElement) {
          const currentScroll = this.wrapperListScroll.nativeElement.scrollTop;
          if (currentScroll > 0) {
            this.snapToNearestPosition(currentScroll);
          }
        }
      }, RETRY_DELAY_MS * (retryCount + 1));
      return window.innerHeight - 52; // fallback: 100dvh - 52px
    }

    const mapPlacementHeight = mapPlacementElement?.offsetHeight || (window.innerHeight - 52); // fallback

    // Top position should be: map-placement height
    // This positions the results section so the list is fully visible
    const topPosition = mapPlacementHeight;

    console.log('📐 Dynamic position calculation:', {
      // searchControlsHeight,
      mapPlacementFound: !!mapPlacementElement,
      calculatedTopPosition: topPosition.toFixed(2)
    });

    return topPosition;
  }

  // Snap to nearest position: 0 (bottom) or dynamically calculated top
  private snapToNearestPosition(currentScrollTop: number) {
    if (!this.wrapperListScroll?.nativeElement) return;

    const scrollElement = this.wrapperListScroll.nativeElement;
    const topPosition = this.calculateTopPosition();
    const bottomPosition = 0;

    // Determine which position is closer
    const distanceToTop = Math.abs(currentScrollTop - topPosition);
    const distanceToBottom = Math.abs(currentScrollTop - bottomPosition);

    const targetPosition = distanceToTop < distanceToBottom ? topPosition : bottomPosition;
    const isMovingToTop = targetPosition === topPosition;

    // Smooth scroll to target position
    scrollElement.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });

    // Update view position state
    this.currentViewPositionSignal.set(isMovingToTop ? 'top' : 'bottom');
    this.currentViewPositionSignal.set(isMovingToTop ? 'top' : 'bottom');

    // After scroll animation completes, check scroll behavior to unlock inner scroll if at top
    if (isMovingToTop) {
      setTimeout(() => {
        console.log('🔓 Checking scroll behavior after snap to top position');
        this.checkScrollBehavior();
      }, 400); // Wait for smooth scroll animation to complete (typically ~300ms)
    }

    console.log('🎯 Snapping to position:', {
      from: currentScrollTop.toFixed(2),
      to: targetPosition.toFixed(2),
      target: isMovingToTop ? 'top' : 'bottom',
      willUnlockScroll: isMovingToTop
    });
  }

  // Move to bottom position (scroll to 0) when map interactions occur
  private moveResultsSectionToBottomOnMapInteraction() {
    if (!this.wrapperListScroll?.nativeElement) return;

    const scrollElement = this.wrapperListScroll.nativeElement;

    // Smooth scroll to bottom position (0)
    scrollElement.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    // Update view position state
    this.currentViewPositionSignal.set('bottom');

    console.log('🗺️ Moving to bottom position due to map interaction');
  }

  // Initialize scroll position (start at bottom)
  private initializeResultsPosition() {
    setTimeout(() => {
      if (this.wrapperListScroll?.nativeElement) {
        const scrollElement = this.wrapperListScroll.nativeElement;
        scrollElement.scrollTop = 0; // Start at bottom position
        this.currentViewPositionSignal.set('bottom'); // Initialize signal
        console.log('🎯 Initialized scroll at bottom position (0)');
      }
    }, 100); // Small delay to ensure DOM is ready
  }

  /**
   * Load global reference data from API
   * Point types and capability types are the source of truth from backend
   */
  private loadGlobalReferenceData() {
    // Load point types if not already loaded
    if (pointTypesState.needsLoad()) {
      console.log('🔄 Loading point types from API...');
      pointTypesState.setLoading(true);

      this.pointsApiService.getPointTypes().subscribe({
        next: (types) => {
          pointTypesState.setTypes(types);
          console.log('✅ Point types loaded into global state:', types.length, 'types');
        },
        error: (error) => {
          pointTypesState.setError(error.message || 'Failed to load point types');
          console.error('❌ Failed to load point types:', error);
        }
      });
    }

    // Load capability types if not already loaded
    if (capabilityTypesState.needsLoad()) {
      console.log('🔄 Loading capability types from API...');
      capabilityTypesState.setLoading(true);

      this.pointsApiService.getCapabilityTypes().subscribe({
        next: (types) => {
          capabilityTypesState.setTypes(types);
          console.log('✅ Capability types loaded into global state:', types.length, 'types');
        },
        error: (error) => {
          capabilityTypesState.setError(error.message || 'Failed to load capability types');
          console.error('❌ Failed to load capability types:', error);
        }
      });
    }
  }

  async loadRecyclingPoints() {
    try {
      // Check if we're in browser environment
      if (!isPlatformBrowser(this.platformId)) {
        console.log('Server-side rendering detected, skipping fetch');
        return;
      }

      // Set loading state
      this.isLoadingPoints.set(true);

      // 1. Hydrate from IndexedDB cache for instant display
      try {
        const cached = await this.pointsCache.getAll();
        if (cached.length > 0) {
          console.log(`[PointsCache] Hydrated ${cached.length} points from cache`);
          this.recyclingPoints = cached;
          this.updatePointSignals();
        } else {
          // No cache yet — fall back to local JSON for first-time users
          this.loadLocalPointsData();
        }
      } catch (cacheErr) {
        console.warn('[PointsCache] Hydration failed, falling back to local JSON', cacheErr);
        this.loadLocalPointsData();
      }

      // 2. Fetch fresh from API, upsert to cache, merge
      try {
        await this.loadPointsFromApi();
      } catch (apiError) {
        console.warn('API failed - keeping cached/local data', apiError);
      } finally {
        this.isLoadingPoints.set(false);
      }
    } catch (error) {
      console.error('Error loading recycling points:', error);
      this.isLoadingPoints.set(false);
      this.loadFallbackData();
    }
  }

  /**
   * Load points from local JSON file (immediate display)
   */
  private loadLocalPointsData(): void {
    console.log('📦 Loading local points data...');
    this.http.get<{ points: any[] }>('assets/data/points-api-response.json').subscribe({
      next: (response) => {
        const points = response.points || [];
        if (points.length > 0) {
          this.recyclingPoints = points.map(point => {
            const openStatus = this.calculateOpenStatus(point.schedules || []);
            return {
              ...point,
              position: point.latitude && point.longitude ? {
                lat: point.latitude,
                lng: point.longitude
              } : undefined,
              isCurrentlyOpen: openStatus.isOpen,
              opensToday: openStatus.opensToday,
              hours: this.formatHours(point.schedules || [])
            };
          });
          console.log('✅ Local data loaded:', this.recyclingPoints.length, 'points');
          this.updatePointSignals();
        }
      },
      error: (error) => {
        console.warn('⚠️ Failed to load local JSON data:', error);
        // Fallback data will be used if API also fails
      }
    });
  }

  /**
   * Load points from the backend API.
   * After fetching, upserts to IndexedDB cache and merges fresh + cached points.
   */
  private async loadPointsFromApi(): Promise<void> {
    console.log('Loading points from API...');

    // Build params: include location center + radius if the map has been initialized
    const loc = this.mapLocation();
    const params: any = {
      limit: 500,
      isActive: true,
      ...(loc && {
        latitude: loc.lat,
        longitude: loc.lng,
        radius: loc.radiusKm,
      }),
    };

    return new Promise((resolve, reject) => {
      this.pointsApiService.getAllPoints(params).subscribe({
        next: async (response) => {
          // Handle both possible response structures: {data: [...]} or {points: [...]}
          const points = response.data || response.points || [];

          if (points && points.length > 0) {
            // Add computed UI properties to each point
            const freshPoints: RecyclingPoint[] = points.map((point: any) => {
              const openStatus = this.calculateOpenStatus(point.schedules || []);
              return {
                ...point,
                position: point.latitude && point.longitude ? {
                  lat: point.latitude,
                  lng: point.longitude
                } : undefined,
                isCurrentlyOpen: openStatus.isOpen,
                opensToday: openStatus.opensToday,
                hours: this.formatHours(point.schedules || [])
              };
            });

            console.log(`API returned ${freshPoints.length} points`);

            // Upsert fresh points into IndexedDB cache, then merge
            try {
              await this.pointsCache.upsertMany(freshPoints);
              const allCached = await this.pointsCache.getAll();
              this.recyclingPoints = this.pointsCache.merge(freshPoints, allCached);
              console.log(`[PointsCache] Merged: ${freshPoints.length} fresh + ${allCached.length} cached = ${this.recyclingPoints.length} total`);
            } catch (cacheErr) {
              // Cache failed — still use the fresh API data
              console.warn('[PointsCache] Cache merge failed, using API data only', cacheErr);
              this.recyclingPoints = freshPoints;
            }

            this.updatePointSignals();

            // Track the location used for this load (for "Buscar acá" detection)
            this.lastLoadedLocation = loc ?? null;

            resolve();
          } else {
            reject(new Error('No points received from API'));
          }
        },
        error: (error) => {
          console.error('API loading failed:', error);
          reject(error);
        }
      });
    });
  }


  /**
   * Updates the point signals after loading data
   */
  private updatePointSignals(): void {
    this.recyclingPointsSignal.set(this.recyclingPoints);
    // Map markers will update automatically via computed signal
  }

  /**
   * Load fallback data when both API and JSON fail
   */
  private loadFallbackData(): void {
    console.log('📦 Loading fallback data...');
    this.recyclingPoints = [
      {
        id: 'fallback-1',
        name: 'Centro de Reciclaje Barrio Centro',
        entityType: 'reception_point',
        latitude: -32.9442,
        longitude: -60.6505,
        isActive: true,
        address: {
          street: 'Calle Falsa 123',
          city: 'Rosario',
          province: 'Santa Fe'
        },
        schedules: [
          { dayCode: 'mon', openTime: '09:00', closeTime: '17:00' },
          { dayCode: 'tue', openTime: '09:00', closeTime: '17:00' },
          { dayCode: 'wed', openTime: '09:00', closeTime: '17:00' },
          { dayCode: 'thu', openTime: '09:00', closeTime: '17:00' },
          { dayCode: 'fri', openTime: '09:00', closeTime: '17:00' }
        ],
        services: {
          dropOff: true,
          pickUp: false,
          sorting: true,
          cleaning: false,
          repair: false,
          buyback: false
        },
        accessibility: {
          wheelchairAccess: true,
          parkingAvailable: true,
          publicTransport: true,
          assistance: false
        },
        materials: [
          { id: 'papel', name: 'Papel', label: 'Papel', type: 'recyclable', recyclable: true },
          { id: 'plastico', name: 'Plástico', label: 'Plástico', type: 'recyclable', recyclable: true }
        ],
        acceptedMaterials: ['Papel', 'Plástico'],
        hours: 'Lunes a Viernes 9:00 - 17:00',
        position: { lat: -32.9442, lng: -60.6505 },
        isCurrentlyOpen: false
      },
      {
        id: 'fallback-2',
        name: 'Punto Verde Plaza Mayor',
        entityType: 'street_container',
        latitude: -32.9468,
        longitude: -60.6393,
        isActive: true,
        address: {
          street: 'Av. Siempre Viva 456',
          city: 'Rosario',
          province: 'Santa Fe'
        },
        schedules: [
          { dayCode: 'mon', openTime: '08:00', closeTime: '18:00' },
          { dayCode: 'tue', openTime: '08:00', closeTime: '18:00' },
          { dayCode: 'wed', openTime: '08:00', closeTime: '18:00' },
          { dayCode: 'thu', openTime: '08:00', closeTime: '18:00' },
          { dayCode: 'fri', openTime: '08:00', closeTime: '18:00' },
          { dayCode: 'sat', openTime: '08:00', closeTime: '18:00' }
        ],
        services: {
          dropOff: true,
          pickUp: false,
          sorting: false,
          cleaning: false,
          repair: false,
          buyback: false
        },
        accessibility: {
          wheelchairAccess: false,
          parkingAvailable: false,
          publicTransport: true,
          assistance: false
        },
        materials: [
          { id: 'papel', name: 'Vidrio', label: 'Vidrio', type: 'recyclable', recyclable: true },
          { id: 'compost', name: 'Orgánico', label: 'Orgánico', type: 'compostable', recyclable: false }
        ],
        acceptedMaterials: ['Vidrio', 'Orgánico'],
        hours: 'Lunes a Sábado 8:00 - 18:00',
        position: { lat: -32.9468, lng: -60.6393 },
        isCurrentlyOpen: false
      }
    ];
    this.updatePointSignals();
  }


  calculateOpenStatus(schedule: any): { isOpen: boolean; opensToday?: string } {
    if (!schedule) return { isOpen: false };

    // DEBUG MODE: Force some points to be open for testing
    if (this.isProgrammingMode()) {
      // In programming mode, randomly make 30% of points with schedule data appear open
      if (Math.random() < 0.3) {
        return { isOpen: true };
      }
    }

    const now = this.getCurrentTime();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.getHours() * 60 + now.getMinutes(); // Current time in minutes

    // Map day numbers to day codes used in the schedule
    const dayCodeMap = {
      0: 'sun', // Sunday
      1: 'mon', // Monday
      2: 'tue', // Tuesday
      3: 'wed', // Wednesday
      4: 'thu', // Thursday
      5: 'fri', // Friday
      6: 'sat'  // Saturday
    };

    const currentDayCode = dayCodeMap[currentDay as keyof typeof dayCodeMap];

    // Handle array schedule format
    if (Array.isArray(schedule)) {
      // Check if there's any valid schedule data - be more lenient 
      const validschedule = schedule.filter(s => {
        // Just check if it has the basic structure - any of these combinations
        const hasValidStructure = s && (
          (s.dayCode && s.openTime && s.closeTime) ||
          (s.day_code && s.open && s.close) ||
          (s.dayCode && s.open && s.close) ||
          (s.day_code && s.openTime && s.closeTime)
        );
        return hasValidStructure;
      });
      // If we have any schedule data at all, consider it valid
      if (schedule.length > 0 && validschedule.length === 0) {
        return { isOpen: false };
      }

      if (validschedule.length === 0) {
        return { isOpen: false };
      }

      // IMPORTANT: Use dayCode, openTime, closeTime as single sources of truth for schedule properties
      const todayschedule = schedule.filter(s =>
        s.dayCode === currentDayCode &&
        s.openTime && s.closeTime
      );

      // console.log(`Checking schedule for ${dayNames[currentDay]} (${currentDayCode}) at ${Math.floor(currentTime/60)}:${(currentTime%60).toString().padStart(2, '0')}`);
      // console.log('Today schedule:', todayschedule);
      if (todayschedule.length === 0) {
        return { isOpen: false };
      }
      // Check if currently within any of today's open periods
      for (const todaySchedule of todayschedule) {
        const openTime = this.parseTimeToMinutes(todaySchedule.openTime);
        const closeTime = this.parseTimeToMinutes(todaySchedule.closeTime);
        if (openTime !== null && closeTime !== null) {
          if (currentTime >= openTime && currentTime <= closeTime) {
            return { isOpen: true };
          }
        }
      }

      // Not currently open, but find when it opens today
      const nextOpening = todayschedule
        .map(s => this.parseTimeToMinutes(s.openTime))
        .filter(time => time !== null && time > currentTime)
        .sort((a, b) => a! - b!)[0];

      if (nextOpening !== undefined && nextOpening !== null && !isNaN(nextOpening)) {
        const opensToday = this.formatMinutesToTime(nextOpening);
        return { isOpen: false, opensToday };
      }

      return { isOpen: false };
    }

    // Handle legacy object format or string format
    if (typeof schedule === 'object') {
      // Check if it has any meaningful schedule data
      const entries = Object.entries(schedule);
      if (entries.length === 0) return { isOpen: false };
      // This is a simplified check for legacy format
      return { isOpen: false };
    }
    if (typeof schedule === 'string') {
      // For string schedule, check if it's meaningful
      if (schedule.toLowerCase().includes('no disponible') || schedule.trim() === '') {
        return { isOpen: false };
      }
      return { isOpen: false };
    }
    return { isOpen: false };
  }

  parseTimeToMinutes(timeString: string): number | null {
    if (!timeString) return null;

    // Handle formats like "9:00 AM", "14:30", "9:00", "9 AM", etc.
    const timeMatch = timeString.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
    if (!timeMatch) {
      console.warn('Could not parse time:', timeString);
      return null;
    }

    let hours = parseInt(timeMatch[1]);
    const minutes = parseInt(timeMatch[2] || '0'); // Default to 0 if no minutes
    const ampm = timeMatch[3]?.toUpperCase();

    // Check for invalid parsed numbers
    if (isNaN(hours) || isNaN(minutes)) {
      console.warn('Invalid time numbers parsed from:', timeString);
      return null;
    }

    // Convert 12-hour to 24-hour format
    if (ampm) {
      if (ampm === 'AM' && hours === 12) {
        hours = 0;
      } else if (ampm === 'PM' && hours !== 12) {
        hours += 12;
      }
    }

    const result = hours * 60 + minutes;

    // Final validation of result
    if (isNaN(result) || result < 0 || result >= 1440) { // 1440 minutes = 24 hours
      console.warn('Invalid time result for:', timeString, 'result:', result);
      return null;
    }

    // console.log(`Parsed time "${timeString}" to ${hours}:${minutes.toString().padStart(2, '0')} (${result} minutes)`);
    return result;
  }



  formatMinutesToTime(minutes: number): string {
    // Handle NaN or invalid input
    if (isNaN(minutes) || minutes < 0) {
      return 'N/A';
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  formatHours(horarios: any): string {
    if (!horarios) return this.t.instant('home.hoursNotAvailable');
    if (typeof horarios === 'string') return horarios;

    // Handle new schedule array format
    if (Array.isArray(horarios)) {
      if (horarios.length === 0) return this.t.instant('home.hoursNotAvailable');

      // Map day codes to translated day names
      const dayCodeToName: Record<string, string> = {
        'mon': this.t.instant('days.monday'),
        'tue': this.t.instant('days.tuesday'),
        'wed': this.t.instant('days.wednesday'),
        'thu': this.t.instant('days.thursday'),
        'fri': this.t.instant('days.friday'),
        'sat': this.t.instant('days.saturday'),
        'sun': this.t.instant('days.sunday')
      };

      // Group by day to handle multiple periods
      const dayGroups = horarios.reduce((acc: any, schedule: any) => {
        // Handle the exact API structure: dayCode field
        const dayCode = schedule.dayCode;
        const dayName = dayCodeToName[dayCode as keyof typeof dayCodeToName] || dayCode || schedule.name;

        if (!acc[dayName]) acc[dayName] = [];

        // Handle the exact API structure: openTime and closeTime fields
        const openTime = schedule.openTime;
        const closeTime = schedule.closeTime;

        // Only add valid periods with both open and close times
        if (openTime && closeTime) {
          // Include note if available
          const noteText = schedule.note ? ` (${schedule.note})` : '';
          acc[dayName].push(`${openTime} - ${closeTime}${noteText}`);
        }
        return acc;
      }, {});

      // Sort days in logical order
      const dayOrder = [
        this.t.instant('days.monday'),
        this.t.instant('days.tuesday'),
        this.t.instant('days.wednesday'),
        this.t.instant('days.thursday'),
        this.t.instant('days.friday'),
        this.t.instant('days.saturday'),
        this.t.instant('days.sunday')
      ];
      const sortedDays = Object.keys(dayGroups).sort((a, b) => {
        const indexA = dayOrder.indexOf(a);
        const indexB = dayOrder.indexOf(b);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      });

      // Format each day with its time periods, handling multiple periods elegantly
      const formattedDays = sortedDays.map(day => {
        const times = dayGroups[day];
        if (times.length === 1) {
          return `${day}: ${times[0]}`;
        } else if (times.length === 2) {
          // Common case: morning and afternoon periods
          return `${day}: ${times[0]} y ${times[1]}`;
        } else {
          return `${day}: ${times.join(', ')}`;
        }
      });

      return formattedDays.join(' | ');
    }

    // Handle legacy object format
    if (typeof horarios === 'object') {
      const entries = Object.entries(horarios);
      if (entries.length === 0) return this.t.instant('home.hoursNotAvailable');
      if (entries.length === 1 && entries[0][0] === 'Domingo' && entries[0][1] === 'cerrado') {
        return this.t.instant('home.monToSatNoSchedule');
      }
      return entries.map(([day, hours]) => `${day}: ${hours}`).join(', ');
    }

    return this.t.instant('home.hoursNotAvailable');
  }

  /**
   * Get marker color based on point type.
   * Source of truth for colors is styles.scss:
   * --color-place: var(--color-primary)      → places/points
   * --color-container: var(--color-secondary) → containers
   * --color-event: var(--color-info)          → events
   * --color-route: var(--color-secondary)     → routes/areas
   */
  getMarkerColor(type: string): string {
    const cacheKey = `type:${type}`;
    if (this.markerColorCache.has(cacheKey)) {
      return this.markerColorCache.get(cacheKey)!;
    }

    // Get color from CSS variable based on type
    let cssVar = '--color-place'; // default
    switch (type) {
      case 'container':
        cssVar = '--color-container';
        break;
      case 'event':
        cssVar = '--color-event';
        break;
      case 'route':
      case 'area':
        cssVar = '--color-route';
        break;
      default:
        cssVar = '--color-place';
        break;
    }

    if (!this.platform.isBrowser || !this.platform.documentElement) return '#71a67b';
    const computedStyle = this.platform.getComputedStyle(this.platform.documentElement);
    const color = computedStyle?.getPropertyValue(cssVar).trim() || '#71a67b';
    this.markerColorCache.set(cacheKey, color);
    return color;
  }

  // Method to add selected point at the beginning of the filtered list
  private addSelectedPointToTop(points: RecyclingPoint[]): RecyclingPoint[] {
    const selectedId = this.selectedPointIdSignal();
    const isSelectedFromMap = this.isSelectedFromMapSignal();
    const userLoc = this.userLocation();
    console.log('selectedId', selectedId, 'isSelectedFromMap', isSelectedFromMap);

    // If user has location, sort by distance first (unless there's a selection from map)
    let sortedPoints = points;
    if (userLoc && (!selectedId || !isSelectedFromMap)) {
      sortedPoints = this.locationService.sortByDistance(points);
    }

    // Only reorder if selected from map
    if (!selectedId || !isSelectedFromMap) {
      return sortedPoints;
    }

    const selectedPoint = sortedPoints.find(point => point.id === selectedId);
    if (!selectedPoint) {
      return sortedPoints;
    }
    console.log('selectedPoint', selectedPoint);

    // Remove the selected point from its original position and add it at the top
    const pointsWithoutSelected = sortedPoints.filter(point => point.id !== selectedId);
    return [selectedPoint, ...pointsWithoutSelected];
  }

  // Unified map interaction handler
  onMapInteraction(event: {
    type: 'click' | 'move' | 'zoom' | 'markerClick' | 'containerClick' | 'dragstart' | 'dragend' | 'touchstart' | 'touchend' | 'longPress',
    data?: any,
    marker?: any,
    screenPosition?: { x: number, y: number },
    coordinates?: { lat: number, lng: number },
    originalEvent?: any
  }) {
    // console.log('[Home] map interaction event received:', event.type, event);

    // Blur active input on map click/tap interactions to dismiss keyboard
    if (['click', 'markerClick', 'containerClick', 'dragend', 'touchend'].includes(event.type)) {
      const activeElement = this.platform.activeElement as HTMLElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' ||
        activeElement.closest('ion-input, ion-textarea, app-input'))) {
        activeElement.blur();
      }
    }

    // Move results section to bottom on significant map interactions
    // if (['click', 'dragstart', 'zoom', 'touchstart'].includes(event.type)) {
    if (['markerClick', 'click', 'containerClick'].includes(event.type)) {
      // this.moveResultsSectionToBottomOnMapInteraction();
    }

    switch (event.type) {
      case 'containerClick':
        // Legacy/backward-compatible handler: redirect to unified markerClick logic
        console.log('[Home] containerClick received, routing through unified handler');
        if (event.marker) {
          const point = event.marker.data as RecyclingPoint;

          this.posthog.capture('home_marker_click', {
            pointId: point.id,
            pointName: point.name,
            source: 'map'
          });

          this.selectedPointId = point.id;
          this.isSelectedFromMapSignal.set(true);

          if (this.mapComponent) {
            this.mapComponent.updateSelectedMarker(point.id);
          }

          this.cdr.detectChanges();

          if (this.recyclingFiltersComponent) {
            this.recyclingFiltersComponent.focusOutside();
          }

          this.openEntityModal(point, 'map');
        }
        break;

      case 'markerClick':
        if (event.marker) {
          const point = event.marker.data as RecyclingPoint;

          this.posthog.capture('home_marker_click', {
            pointId: point.id,
            pointName: point.name,
            source: 'map'
          });

          this.openEntityModal(point, 'map');

          this.selectedPointId = point.id;
          this.isSelectedFromMapSignal.set(true); // Mark as selected from map

          // Update the map component's selected marker immediately
          if (this.mapComponent) {
            this.mapComponent.updateSelectedMarker(point.id);
          }
          // Trigger change detection to ensure UI updates
          this.cdr.detectChanges();

          // Blur the search input when interacting with markers
          if (this.recyclingFiltersComponent) {
            this.recyclingFiltersComponent.focusOutside();
          }
          // Set the selected point immediately for non-container types
        }
        break;

      case 'longPress':
        console.log('[Home] handling longPress event', event);
        if (event.coordinates) {
          this.showMapContextMenu(event.coordinates);
        }
        break;

      case 'click':
      case 'touchstart': {
        const clickedContainer = event.data?.features?.some((feature: any) => {
          const layerId = feature?.layer?.id;
          return typeof layerId === 'string' && layerId.includes('container');
        }) ?? false;

        if (clickedContainer) {
          console.log('[Home] click event originated from container layer, skipping popup hide');
          break;
        }

        // Blur the search input if it's focused
        if (this.recyclingFiltersComponent) {
          this.recyclingFiltersComponent.focusOutside();
        }
        if (this.showContainerPopup) {
          this.hideContainerPopup();
        }
        break;
      }
      case 'dragstart':
      case 'zoom':
        // These interactions should hide the container popup if it's open
        break;
      case 'move':
        // Save map state (lat, lng, zoom) on moveend
        if (event.coordinates && event.data?.zoom != null) {
          this.uiSettings.setMapState({
            lat: event.coordinates.lat,
            lng: event.coordinates.lng,
            zoom: event.data.zoom
          });
        }
        // Debounced update of viewport bounds for type counts
        if (this.mapBoundsDebounceTimer) {
          clearTimeout(this.mapBoundsDebounceTimer);
        }
        this.mapBoundsDebounceTimer = setTimeout(() => {
          const map = this.mapComponent?.getMap();
          if (map) {
            const b = map.getBounds();
            const newBounds = {
              north: b.getNorth(),
              south: b.getSouth(),
              east: b.getEast(),
              west: b.getWest()
            };
            this.mapBounds.set(newBounds);

            // Compute center + radius from viewport bounds
            const centerLat = (newBounds.north + newBounds.south) / 2;
            const centerLng = (newBounds.east + newBounds.west) / 2;
            // Approximate radius: half the diagonal of the viewport in km
            const latSpan = Math.abs(newBounds.north - newBounds.south);
            const lngSpan = Math.abs(newBounds.east - newBounds.west);
            const diagDeg = Math.sqrt(latSpan * latSpan + lngSpan * lngSpan);
            const radiusKm = (diagDeg / 2) * 111.32; // ~111.32 km per degree
            this.mapLocation.set({ lat: centerLat, lng: centerLng, radiusKm });

            // Show "Buscar acá" in list when viewport moved significantly from last load
            if (this.lastLoadedLocation) {
              const moved = this.hasViewportMovedSignificantly(this.lastLoadedLocation, { lat: centerLat, lng: centerLng, radiusKm });
              this.showSearchHereButton.set(moved);
            }
          }
        }, 500);
        break;
    }
  }

  // Show popup for container information
  showContainerInfoPopup(point: RecyclingPoint, screenPosition?: { x: number, y: number }) {
    // Start animation to hide results section
    console.log('[Home] showContainerInfoPopup invoked', { pointId: point?.id, screenPosition });

    this.containerPopupData = point;
    this.showContainerPopup = true;
    if (screenPosition && this.platform.isBrowser) {
      // Get map container element to calculate actual dimensions
      const mapElement = this.platform.querySelector<HTMLElement>('app-maplibre-map');

      if (mapElement) {
        // }
      }

      const popupWidth = 180;
      const popupHeight = 80;
      // Calculate position based on mode
      let x: number, y: number;
      x = screenPosition.x + 20;
      y = screenPosition.y - 60;

      // Bounds checking against viewport
      if (x + popupWidth > this.platform.innerWidth) {
        x = screenPosition.x - popupWidth - 20;
      }
      if (y < 0) {
        y = screenPosition.y + 20;
      }
      if (y + popupHeight > this.platform.innerHeight) {
        y = this.platform.innerHeight - popupHeight - 10;
      }
      // }

      this.containerPopupPosition = { x, y };
    } else {
      console.log('[Home] using fallback popup position');
      if (!this.platform.isBrowser) {
        this.containerPopupPosition = { x: 0, y: 0 };
      } else {
        const fallbackX = this.platform.innerWidth / 2 - 90; // Always fullscreen now
        const fallbackY = this.platform.innerHeight / 2 - 40; // Always fullscreen now
        this.containerPopupPosition = { x: fallbackX, y: fallbackY };
      }
    }
  }

  // Hide container popup
  hideContainerPopup() {
    console.log('[Home] hideContainerPopup invoked');
    this.showContainerPopup = false;
    this.containerPopupData = null;

    // Blur the search input when hiding container popup
    if (this.recyclingFiltersComponent) {
      this.recyclingFiltersComponent.focusOutside();
    }

    // Animate results section back in
    // this.animateResultsSectionIn();
  }

  // Handle map interactions (clicks, drags, etc.) to hide container popup
  // Get simplified address (street only, no city/province)
  getSimplifiedAddress(fullAddress: string): string {
    if (!fullAddress) return this.t.instant('home.locationNotAvailable');

    // Split by common separators (comma, dash, pipe)
    const parts = fullAddress.split(/[,\-|]/);

    // Return first part (usually the street)
    return parts[0]?.trim() || fullAddress;
  }

  // Method to handle clicking on a list item
  onPointClick(point: RecyclingPoint) {
    this.posthog.capture('home_point_click', {
      pointId: point.id,
      pointName: point.name,
      source: 'list'
    });
    this.selectedPointId = point.id;
    this.isSelectedFromMapSignal.set(false); // Mark as selected from list, not map

    // Update the map component's selected marker immediately
    if (this.mapComponent) {
      this.mapComponent.updateSelectedMarker(point.id);

      // Point the map on the selected marker with animation
      if (point.position) {
        console.log('Selected point from list:', point);
        this.mapComponent.setCenter(point.position, undefined, true);
      }
    }

    // Trigger change detection to ensure UI updates
    this.cdr.detectChanges();

    // Open the appropriate modal based on point type
    this.openEntityModal(point, 'list');
  }

  // Method to check if a point is selected
  isPointSelected(point: RecyclingPoint): boolean {
    return this.selectedPointId === point.id;
  }

  // Highlight marker on map when hovering a list card
  onPointHover(point: RecyclingPoint): void {
    if (this.mapComponent) {
      this.mapComponent.highlightMarkerFromList(point.id);
    }
  }

  onPointHoverLeave(): void {
    if (this.mapComponent) {
      this.mapComponent.highlightMarkerFromList(null);
    }
  }

  // Handle fullscreen change
  // onFullscreenChange(isFullscreen: boolean): void {
  //   this.isMapFullscreen = isFullscreen;

  //   // Block/unblock page scrolling when entering/exiting fullscreen
  //   if (isPlatformBrowser(this.platformId)) {
  //     const htmlElement = document.documentElement;
  //     const bodyElement = document.body;

  //     if (isFullscreen) {
  //       // Add fullscreen classes to block scrolling
  //       htmlElement.classList.add('fullscreen-active');
  //       bodyElement.classList.add('fullscreen-active');
  //     } else {
  //       // Remove fullscreen classes to restore scrolling
  //       htmlElement.classList.remove('fullscreen-active');
  //       bodyElement.classList.remove('fullscreen-active');
  //     }
  //   }
  // }

  search(): void {
    // TODO: Implement search functionality
    console.log('Searching for:', this.location, this.selectedMaterials());
  }

  getMaterialIcon(material: string): string {
    // Get icon from unified global materials state (search in all materials)
    const materialOption = materialsState.availableMaterials().find(option => option.value === material);
    return materialOption?.icon || '📦';
  }

  // Prefer dataset-aware icon resolution (uses mapping helpers)
  getMaterialIconForDisplay(material: string): string {
    /*     const datasetIcon = getIconForDatasetMaterial(material);
        if (datasetIcon) return datasetIcon; */
    return this.getMaterialIcon(material);
  }

  // Get schedule days for display (with caching)
  getScheduleDays(point: RecyclingPoint): { code: string; label: string; isOpen: boolean }[] {
    if (this.scheduleDaysCache.has(point.id)) {
      return this.scheduleDaysCache.get(point.id)!;
    }

    // Return empty array if no schedule data available
    if (!point.schedules || !Array.isArray(point.schedules) || point.schedules.length === 0) {
      const result: { code: string; label: string; isOpen: boolean }[] = [];
      this.scheduleDaysCache.set(point.id, result);
      return result;
    }

    const dayMap = {
      'mon': { label: this.t.instant('days.single.mon'), name: this.t.instant('days.monday') },
      'tue': { label: this.t.instant('days.single.tue'), name: this.t.instant('days.tuesday') },
      'wed': { label: this.t.instant('days.single.wed'), name: this.t.instant('days.wednesday') },
      'thu': { label: this.t.instant('days.single.thu'), name: this.t.instant('days.thursday') },
      'fri': { label: this.t.instant('days.single.fri'), name: this.t.instant('days.friday') },
      'sat': { label: this.t.instant('days.single.sat'), name: this.t.instant('days.saturday') },
      'sun': { label: this.t.instant('days.single.sun'), name: this.t.instant('days.sunday') }
    };

    const allDays = Object.keys(dayMap).map(code => ({
      code,
      label: dayMap[code as keyof typeof dayMap].label,
      isOpen: false
    }));

    // Check if point has schedule data - use the schedules property directly
    if (point.schedules && Array.isArray(point.schedules)) {
      const openDays = new Set(point.schedules.map((s: any) => s.dayCode));
      allDays.forEach(day => {
        day.isOpen = openDays.has(day.code);
      });
    }

    this.scheduleDaysCache.set(point.id, allDays);
    return allDays;
  }

  // Get simplified schedule hours text (with caching)
  getScheduleHoursText(point: RecyclingPoint): string {
    if (this.scheduleHoursCache.has(point.id)) {
      return this.scheduleHoursCache.get(point.id)!;
    }

    // Return empty string if no schedule data available
    if (!point.schedules || !Array.isArray(point.schedules) || point.schedules.length === 0) {
      this.scheduleHoursCache.set(point.id, '');
      return '';
    }

    // Filter out invalid entries and group by day
    const validschedules = point.schedules.filter((s: any) => s.openTime && s.closeTime);

    if (validschedules.length === 0) {
      this.scheduleHoursCache.set(point.id, '');
      return '';
    }

    // Group periods by day
    const dayGroups = validschedules.reduce((acc: any, schedule: any) => {
      const dayName = schedule.dayCode;
      if (!acc[dayName]) acc[dayName] = [];
      const openTime = schedule.openTime;
      const closeTime = schedule.closeTime;
      acc[dayName].push(`${openTime} - ${closeTime}`);
      return acc;
    }, {});

    // Get unique time patterns
    const timePatterns = Object.values(dayGroups).map((times: any) => times.join('\n'));
    const uniquePatterns = [...new Set(timePatterns)];

    let result = '';
    if (uniquePatterns.length === 1) {
      // All days have the same schedule - just show the time pattern without day names
      result = uniquePatterns[0];
    } else {
      // Show the most detailed pattern as example with note on new line
      const longestPattern = uniquePatterns.reduce((a, b) => a.length > b.length ? a : b);
      result = `${longestPattern}\n(horarios varían)`;
    }

    this.scheduleHoursCache.set(point.id, result);
    return result;
  }

  onMaterialSelectionChange(materials: string[]): void {
    // Sync both states: materialsState (for pills display) and filterService (for filtering)
    materialsState.setSelectedMaterials(materials);
    this.filterService.setSelectedMaterials(materials);
  }

  // Handle material toggle from pills component
  // Updates FilterService to keep it in sync with materialsState
  onMaterialToggle(event: { materialName: string, isSelected: boolean }): void {
    // Sync filterService with the material toggle
    this.filterService.toggleMaterial(event.materialName);
    this.posthog.capture('home_material_toggle', {
      material: event.materialName,
      isSelected: event.isSelected
    });
    // Just log for debugging
    console.log(`Material ${event.materialName} ${event.isSelected ? 'selected' : 'deselected'}`);
  }

  // Handle other material toggle from pills component
  // Updates FilterService to keep it in sync with materialsState
  onOtherMaterialToggle(event: { materialName: string, isSelected: boolean }): void {
    // Sync filterService with the material toggle
    this.filterService.toggleMaterial(event.materialName);
    // Just log for debugging
    console.log(`Other material ${event.materialName} ${event.isSelected ? 'selected' : 'deselected'}`);
  }

  // Toggle open/closed filter - now uses FilterService
  toggleOpenFilter(): void {
    this.posthog.capture('home_toggle_open_filter', {
      newValue: !this.filterService.showOnlyOpen()
    });
    this.filterService.setShowOnlyOpen(!this.filterService.showOnlyOpen());
  }

  // Toggle container visibility filter - now uses FilterService
  toggleContainerFilter(): void {
    this.posthog.capture('home_toggle_container_filter', {
      newValue: !this.filterService.showContainers()
    });
    this.filterService.setShowContainers(!this.filterService.showContainers());
  }

  // Handle search term changes - now uses FilterService
  onSearchChange(searchTerm: string): void {
    this.posthog.capture('home_search', {
      searchTerm: searchTerm,
      searchLength: searchTerm.length
    });
    this.filterService.setSearchTerm(searchTerm);

    // Reset to first page when searching
    this.currentPage.set(1);

    // Add a subtle delay and trigger animations for search results
    setTimeout(() => this.triggerListAnimations(), 150);
  }

  filtersExpanded(expanded: boolean) {
    this.posthog.capture('home_filters_expanded', {
      expanded: expanded
    });
    // Toggle the filters expanded state
    this.isFiltersExpanded = expanded;
    console.log("Filters expanded:", this.isFiltersExpanded);

    // Close any open modal when filters are expanded (only on mobile)
    if (expanded && !this.isDesktopViewport()) {
      this.customModalCtrl.getTop().then(modal => {
        if (modal) {
          console.log("Closing modal due to filters expansion (mobile)");
          this.customModalCtrl.dismiss(null, 'filters-expanded');
        }
      });
    }

  }

  /**
   * Called when user explicitly clicks the 'Buscar' button.
   * Fits the map to show all filtered markers if none are currently visible.
   */
  onSearchRequested(): void {
    if (!this.mapComponent) return;
    const markers = this.filteredMapMarkers();
    if (markers.length > 0 && !this.mapComponent.hasVisibleMarkers(markers)) {
      // Small delay to let the filter panel collapse animation start
      setTimeout(() => this.mapComponent.fitToMarkers(markers), 200);
    }
  }

  // Toggle schedule entities visibility
  toggleroutePoints(): void {
    this.posthog.capture('home_toggle_schedule_entities', {
      newValue: !this.showroutePoints()
    });
    const wasPreviouslyExpanded = this.showroutePoints();
    this.showroutePoints.set(!this.showroutePoints());
    const isNowExpanded = this.showroutePoints();

    console.log('🗂️ 🎯 SCHEDULE ENTITIES TOGGLE TRIGGERED:', {
      'User Action': 'Clicked toggle schedule entities button',
      'State Change': {
        from: wasPreviouslyExpanded ? 'expanded' : 'collapsed',
        to: isNowExpanded ? 'expanded' : 'collapsed'
      },
      'Layout Impact': 'This will change DOM height and may affect list position',
      'Next Action': 'Will trigger scroll behavior check after DOM update (50ms delay)'
    });

    // Give time for DOM updates, then check scroll behavior
    setTimeout(() => {
      console.log('🔄 🎯 POST-TOGGLE SCROLL BEHAVIOR CHECK:');
      console.log('🔄 Trigger: DOM layout change from schedule entities toggle');
      this.checkScrollBehavior();
    }, 50);
  }

  // Toggle location component expanded state
  expandLocationComponent(expanded: boolean): void {
    this.posthog.capture('home_expand_location_component', {
      expanded: expanded
    });
    this.expandedLocationComponent = expanded;
  }

  // Handle location selection from location input
  // Note: LocationService is now updated directly by LocationInputComponent
  onLocationSelected(location: { lat: number; lng: number; displayName: string }): void {
    this.posthog.capture('home_location_selected', {
      displayName: location.displayName,
      hasCoordinates: !!(location.lat && location.lng)
    });
    console.log('📍 Location selected in home:', location);

    // Point map on selected location with animation
    if (this.mapComponent) {
      this.mapComponent.setCenter(
        { lat: location.lat, lng: location.lng },
        15, // Zoom level 15 for street view
        true // Animate the transition
      );
    }

    // Load nearby points from the selected location
    this.loadNearbyPoints(location.lat, location.lng);

    // Optionally close the location component after selection
    this.expandedLocationComponent = false;

    console.log('✅ User location updated to:', location.displayName);
  }

  // Clear all filters - now uses FilterService
  clearAllFilters(): void {
    this.posthog.capture('home_clear_all_filters', {
      hadMaterialsSelected: this.selectedMaterials().length > 0,
      hadOpenFilter: this.filterService.showOnlyOpen(),
      hadContainerFilter: this.filterService.showContainers(),
      hadSearchTerm: this.filterService.searchTerm().length > 0
    });
    materialsState.clearSelectedMaterials();
    this.filterService.clearFilters();
    this.currentPage.set(1); // Reset to first page when clearing filters

    // Trigger animations for refreshed content
    setTimeout(() => this.triggerListAnimations(), 100);
  }

  // Pagination methods
  nextPage(): void {
    this.posthog.capture('home_pagination_next', {
      currentPage: this.currentPage(),
      totalPages: this.totalPages()
    });
    const current = this.currentPage();
    const total = this.totalPages();
    if (current < total) {
      this.goToPage(current + 1);
    }
  }

  prevPage(): void {
    this.posthog.capture('home_pagination_prev', {
      currentPage: this.currentPage(),
      totalPages: this.totalPages()
    });
    const current = this.currentPage();
    if (current > 1) {
      this.goToPage(current - 1);
    }
  }

  goToPage(page: number): void {
    this.posthog.capture('home_pagination_goto', {
      targetPage: page,
      currentPage: this.currentPage(),
      totalPages: this.totalPages()
    });
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);

      // Briefly reset and re-trigger animations for new page content
      this.triggerListAnimations();

      // Force change detection for immediate visual feedback
      this.cdr.markForCheck();
    }
  }

  // Generate array of page numbers for slider pagination (keep for backward compatibility)
  getPageNumbers(): number[] {
    const totalPages = this.totalPages();
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Getters for pagination info
  get paginationInfo() {
    return {
      currentPage: this.currentPage(),
      totalPages: this.totalPages(),
      itemsPerPage: this.itemsPerPage,
      totalItems: this.filteredAndSortedRecyclingPoints().length,
      hasNextPage: this.currentPage() < this.totalPages(),
      hasPrevPage: this.currentPage() > 1
    };
  }

  // Helper method for template
  getEndIndex(): number {
    return Math.min(this.paginationInfo.currentPage * this.paginationInfo.itemsPerPage, this.paginationInfo.totalItems);
  }

  // Time picker methods for testing
  getCurrentTime(): Date {
    return this.useRealTime ? new Date() : (this.testTime || new Date());
  }

  getCurrentTimeString(): string {
    const time = this.getCurrentTime();
    return `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;
  }

  getCurrentDayOfWeek(): number {
    return this.getCurrentTime().getDay();
  }

  getCurrentTimeDisplay(): string {
    const time = this.getCurrentTime();
    const dayKeys = ['days.sunday', 'days.monday', 'days.tuesday', 'days.wednesday', 'days.thursday', 'days.friday', 'days.saturday'];
    const dayName = this.t.instant(dayKeys[time.getDay()]);
    const timeString = this.getCurrentTimeString();
    return `${dayName}, ${timeString}${this.useRealTime ? ` ${this.t.instant('home.realDateTime')}` : ` ${this.t.instant('home.testDateTime')}`}`;
  }

  onTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const timeValue = input.value;

    if (timeValue) {
      const [hours, minutes] = timeValue.split(':').map(Number);

      // Initialize test time if it doesn't exist
      if (!this.testTime) {
        this.testTime = new Date();
      }

      this.testTime.setHours(hours, minutes, 0, 0);
      this.useRealTime = false;

      // Recalculate open/closed status for all points
      this.updatePointStatuses();
    }
  }

  onDayChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const dayValue = parseInt(select.value);

    // Initialize test time if it doesn't exist
    if (!this.testTime) {
      this.testTime = new Date();
    }

    // Calculate the difference in days to adjust the date
    const currentDay = this.testTime.getDay();
    const dayDifference = dayValue - currentDay;
    const newDate = new Date(this.testTime);
    newDate.setDate(newDate.getDate() + dayDifference);

    this.testTime = newDate;
    this.useRealTime = false;

    // Recalculate open/closed status for all points
    this.updatePointStatuses();
  }

  resetToCurrentTime(): void {
    this.useRealTime = true;
    this.testTime = null;

    // Recalculate open/closed status for all points
    this.updatePointStatuses();
  }

  // Font selector methods for programming mode
  getCurrentFontFamily(): string {
    const body = this.platform.body;
    if (body) {
      const fontClasses = ['font-crimson', 'font-system', 'font-raleway', 'font-serif', 'font-sans', 'font-monospace'];
      for (const fontClass of fontClasses) {
        if (body.classList.contains(fontClass)) {
          return fontClass;
        }
      }
    }
    return 'font-crimson'; // Default
  }

  getCurrentFontSize(): string {
    const body = this.platform.body;
    if (body) {
      const sizeClasses = ['font-size-xs', 'font-size-sm', 'font-size-md', 'font-size-lg', 'font-size-xl', 'font-size-xxl'];
      for (const sizeClass of sizeClasses) {
        if (body.classList.contains(sizeClass)) {
          return sizeClass;
        }
      }
    }
    return 'font-size-md'; // Default
  }

  onFontFamilyChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.uiSettings.setFontFamily(select.value);
  }

  onFontSizeChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.uiSettings.setFontSize(select.value);
  }

  private updatePointStatuses(): void {
    this.recyclingPoints.forEach(point => {
      const pointWithRaw = point as RecyclingPoint & { raw?: any };
      const openStatus = this.calculateOpenStatus(pointWithRaw.raw?.horarios || pointWithRaw.raw?.schedules);
      point.isCurrentlyOpen = openStatus.isOpen;
      point.opensToday = openStatus.opensToday;
    });
    // Update the signal with the updated points
    this.recyclingPointsSignal.set([...this.recyclingPoints]);
  }

  // Helper method to get filter status information
  get filterInfo(): { isFiltering: boolean; totalPoints: number; filteredPoints: number; selectedMaterials: string[]; showOnlyOpen: boolean; searchTerm: string } {
    const selectedMaterials = this.selectedMaterials();
    const selectedMaterialValues = selectedMaterials.map(m => m.value).filter(Boolean) as string[];
    const searchTerm = this.filterService.searchTerm();
    const isFiltering = selectedMaterials.length > 0 || this.filterService.showOnlyOpen() || searchTerm.trim().length > 0;
    return {
      isFiltering,
      totalPoints: this.recyclingPointsSignal().length,
      filteredPoints: this.filteredAndSortedRecyclingPoints().length,
      selectedMaterials: selectedMaterialValues,
      showOnlyOpen: this.filterService.showOnlyOpen(),
      searchTerm
    };
  }

  // Modal methods
  async openPlaceModal(point: RecyclingPoint, triggeredBy?: 'map' | 'list') {
    this.posthog.capture('home_open_point_modal', {
      pointId: point.id,
      pointName: point.name,
      triggeredBy: triggeredBy || 'unknown',
      type: point.type
    });
    // Validate point data before opening modal
    if (!point || !point.id) {
      console.error('Invalid point data provided to openPlaceModal:', point);
      return;
    }

    console.log("openPlaceModal", point);
    this.animateResultsSectionOut();
    const scheduleDays = this.getScheduleDays(point);
    const scheduleHoursText = this.getScheduleHoursText(point);

    // Desktop: fullscreen (1), Mobile: based on coordinates
    let initialHeight = this.isDesktopViewport() ? 1 : (point.longitude && point.latitude ? 2 : 1);
    //get top position from selected point
    const pointElement = this.platform.getElementById(`point-${point.id}`);
    // let animatefrom: number;
    // if (triggeredBy === 'list' && pointElement) 
    const animatefrom = pointElement?.getBoundingClientRect().top;

    // this.swnav.navigate('/home/modal');
    const modal = await this.customModalCtrl.create({
      component: PlaceModalComponent,
      cssClass: ['remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'recycling-point-modal-class', 'left-pane', 'modal-none-backdrop', 'point-modal-class'],
      animated: false,
      initialHeight: initialHeight,
      animateFrom: triggeredBy == 'list' ? animatefrom : undefined,
      triggeredBy: triggeredBy,
      modalId: point.id,
      showBackdrop: false,
      backdropDismiss: false,
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.PointDetail,
      componentProps: {
        point,
        scheduleDays,
        scheduleHoursText,
      },
    });

    modal.present();

    if (!this.isDesktopViewport)
      this.recyclingFiltersComponent.closeFilter();
    modal.onDidDismiss().then((result) => {
      console.log('Modal dismissed with data:', result);
      if (result.role != 'another-modal-entrance') {
        this.animateResultsSectionIn();
      }
    }
    );
  }

  closeRecyclingPointModal() {
    // This method is no longer needed since the modal handles its own dismissal
    // but keeping it for compatibility
  }

  // Container modal method
  async openContainerModal(container: RecyclingPoint, triggeredBy?: 'map' | 'list') {
    this.posthog.capture('home_open_container_modal', {
      containerId: container.id,
      containerName: container.name,
      triggeredBy: triggeredBy || 'unknown',
      type: container.type
    });

    // Validate container data before opening modal
    if (!container || !container.id) {
      console.error('Invalid container data provided to openContainerModal:', container);
      return;
    }

    console.log("openContainerModal", container);
    this.animateResultsSectionOut();

    // Desktop: fullscreen (1), Mobile: based on coordinates
    let initialHeight = this.isDesktopViewport() ? 1 : (container.longitude && container.latitude ? 2 : 1);

    // Get top position from selected container element
    const containerElement = this.platform.getElementById(`point-${container.id}`);
    const animatefrom = containerElement?.getBoundingClientRect().top;

    const modal = await this.customModalCtrl.create({
      component: ContainerModalComponent,
      cssClass: ['remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'container-modal-class', 'left-pane', 'modal-none-backdrop', 'point-modal-class'],
      animated: false,
      initialHeight: initialHeight,
      animateFrom: triggeredBy == 'list' ? animatefrom : undefined,
      triggeredBy: triggeredBy,
      modalId: container.id,
      showBackdrop: false,
      backdropDismiss: false,
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.PointDetail,
      componentProps: {
        container,
      },
    });

    modal.present();

    if (!this.isDesktopViewport)
      this.recyclingFiltersComponent.closeFilter();

    modal.onDidDismiss().then((result) => {
      console.log('Container modal dismissed with data:', result);
      if (result.role != 'another-modal-entrance') {
        this.animateResultsSectionIn();
      }
    });
  }

  // Route/Area modal method
  async openRouteModal(route: RecyclingPoint, triggeredBy?: 'map' | 'list') {
    this.posthog.capture('home_open_route_modal', {
      routeId: route.id,
      routeName: route.name,
      triggeredBy: triggeredBy || 'unknown',
      type: route.type
    });

    if (!route || !route.id) {
      console.error('Invalid route data provided to openRouteModal:', route);
      return;
    }

    console.log("openRouteModal", route);
    this.animateResultsSectionOut();

    // Desktop: fullscreen (1), Mobile: based on coordinates
    let initialHeight = this.isDesktopViewport() ? 1 : (route.longitude && route.latitude ? 2 : 1);

    const routeElement = this.platform.getElementById(`point-${route.id}`);
    const animatefrom = routeElement?.getBoundingClientRect().top;

    const modal = await this.customModalCtrl.create({
      component: RouteModalComponent,
      cssClass: ['remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'route-modal-class', 'left-pane', 'modal-none-backdrop', 'point-modal-class'],
      animated: false,
      initialHeight: initialHeight,
      animateFrom: triggeredBy == 'list' ? animatefrom : undefined,
      triggeredBy: triggeredBy,
      modalId: route.id,
      showBackdrop: false,
      backdropDismiss: false,
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.PointDetail,
      componentProps: {
        route,
      },
    });

    modal.present();

    if (!this.isDesktopViewport)
      this.recyclingFiltersComponent.closeFilter();

    modal.onDidDismiss().then((result) => {
      console.log('Route modal dismissed with data:', result);
      if (result.role != 'another-modal-entrance') {
        this.animateResultsSectionIn();
      }
    });
  }

  // Event modal method
  async openEventModal(event: RecyclingPoint, triggeredBy?: 'map' | 'list') {
    this.posthog.capture('home_open_event_modal', {
      eventId: event.id,
      eventName: event.name,
      triggeredBy: triggeredBy || 'unknown',
      type: event.type
    });

    if (!event || !event.id) {
      console.error('Invalid event data provided to openEventModal:', event);
      return;
    }

    console.log("openEventModal", event);
    this.animateResultsSectionOut();

    // Desktop: fullscreen (1), Mobile: based on coordinates
    let initialHeight = this.isDesktopViewport() ? 1 : (event.longitude && event.latitude ? 2 : 1);

    const eventElement = this.platform.getElementById(`point-${event.id}`);
    const animatefrom = eventElement?.getBoundingClientRect().top;

    const modal = await this.customModalCtrl.create({
      component: EventModalComponent,
      cssClass: ['remateriar-modal', 'modal-wrapper', 'remateriar-modal-height', 'event-modal-class', 'left-pane', 'modal-none-backdrop', 'point-modal-class'],
      animated: false,
      initialHeight: initialHeight,
      animateFrom: triggeredBy == 'list' ? animatefrom : undefined,
      triggeredBy: triggeredBy,
      modalId: event.id,
      showBackdrop: false,
      backdropDismiss: false,
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.PointDetail,
      componentProps: {
        event,
      },
    });

    modal.present();

    if (!this.isDesktopViewport)
      this.recyclingFiltersComponent.closeFilter();

    modal.onDidDismiss().then((result) => {
      console.log('Event modal dismissed with data:', result);
      if (result.role != 'another-modal-entrance') {
        this.animateResultsSectionIn();
      }
    });
  }

  // Handle route entity click
  onRouteEntityClick(entity: any) {
    this.posthog.capture('home_route_entity_click', {
      entityId: entity.id,
      entityName: entity.name,
      source: 'list'
    });
    console.log('Route entity clicked:', entity);

    // Mark as selected
    this.selectedPointId = entity.id;
    this.isSelectedFromMapSignal.set(false);

    // Update map to show the polygon (via map component's updateSelectedMarker method)
    if (this.mapComponent) {
      this.mapComponent.updateSelectedMarker(entity.id);
    }

    // Point map on polygon if coordinates are available
    if (entity.polygon?.coordinates && entity.polygon.coordinates.length > 0) {
      // Calculate center of polygon
      const coords = entity.polygon.coordinates[0];

      const avgLat = coords.reduce((sum: number, coord: number[]) => sum + coord[1], 0) / coords.length;
      const avgLng = coords.reduce((sum: number, coord: number[]) => sum + coord[0], 0) / coords.length;

      if (this.mapComponent) {
        this.mapComponent.setCenter({ lat: avgLat, lng: avgLng }, 14, true);
      }
    } else if (entity.position) {
      // Fallback to point position for Point geometry routes
      if (this.mapComponent) {
        this.mapComponent.setCenter(entity.position, undefined, true);
      }
    }

    // Trigger change detection to ensure UI updates
    this.cdr.detectChanges();

    // Open the route modal
    this.openEntityModal(entity, 'list');
  }

  // Helper method to trigger list animations when content changes
  private triggerListAnimations(): void {
    return; // TEMP DISABLE DEV CODE HARD CODE
    // Temporarily reset animation classes to re-trigger them
    const cards = this.platform.querySelectorAll<Element>('.recycling-card');
    cards?.forEach((card) => {
      const element = card as HTMLElement;
      element.style.animation = 'none';
      element.style.opacity = '0';
      // Re-trigger animation with a slight delay
      setTimeout(() => {
        element.classList.add('animate-staggered');
      }, 10);
    });
  }

  // Trigger entrance animations for the entire layout
  private triggerEntranceAnimations(): void {
    console.log('🎬 Triggering entrance animations');

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      this.initClass = true;
      console.log('✨ Entrance animations activated');
    }, 50);

    // Optional: Add observer for elements entering viewport
    if (isPlatformBrowser(this.platformId)) {
      this.setupIntersectionObserver();
    }
  }

  // Setup intersection observer for viewport-based animations
  private setupIntersectionObserver(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target as HTMLElement;
            element.classList.add('animate-viewport-enter');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe elements that should animate when entering viewport
    setTimeout(() => {
      const elementsToObserve = this.platform.querySelectorAll<Element>('.schedule-entities-section, .recycling-card');
      elementsToObserve?.forEach((el) => observer.observe(el));
    }, 100);
  }

  // Initialize DOM element references
  private initializeDOMReferences(): void {
    // Wait for DOM to be ready
    setTimeout(() => {
      this.searchSection = this.platform.querySelector<HTMLElement>('.search-section');

      console.log('🔗 DOM references initialized:', {
        resultsSection: !!this.resultsSection?.nativeElement,
        searchSection: !!this.searchSection
      });
    }, 50);
  }

  // Refresh DOM references if needed (e.g., after dynamic content changes)
  private refreshDOMReferences(): void {
    if (!this.searchSection) {
      this.searchSection = this.platform.querySelector<HTMLElement>('.search-section');
    }
  }



  // Results section animation methods
  private animateResultsSectionOut(): void {
    // if (this.isResultsSectionAnimating) return;

    // this.isResultsSectionAnimating = true;
    this.isResultsSectionVisible = false;

    // Also animate search section out
    // this.animateSearchSectionOut();

    // Refresh DOM reference if not available
    if (!this.resultsSection) {
      this.refreshDOMReferences();
    }

    if (this.resultsSection?.nativeElement) {
      console.log("Results section animation out triggered", this.resultsSection.nativeElement);
      this.resultsSection.nativeElement.classList.remove('animate-fade-in');
      this.resultsSection.nativeElement.classList.add('animate-fade-out');
    } else {
      console.warn('Results section element not found for animation out');
    }
  }

  private animateResultsSectionIn(): void {
    if (this.isResultsSectionAnimating) return;

    // this.isResultsSectionAnimating = true; // DEV CODE HARD CODE

    // Small delay to ensure modal is hidden first
    // setTimeout(() => {
    this.isResultsSectionVisible = true;

    // Also animate search section back in
    // this.animateSearchSectionIn();

    // Refresh DOM reference if not available
    if (!this.resultsSection) {
      this.refreshDOMReferences();
    }

    if (this.resultsSection?.nativeElement) {
      this.resultsSection.nativeElement.classList.remove('animate-fade-out');
      this.resultsSection.nativeElement.classList.add('animate-fade-in');
      console.log("Results section animation in triggered", this.resultsSection.nativeElement);

      // Reset animation state after animation completes
      setTimeout(() => {
        this.isResultsSectionAnimating = false;
      }, 300); // Match CSS animation duration
    } else {
      console.warn('Results section element not found for animation in');
    }
    // }, 1);
  }

  // Search section animation methods
  // Toggle view methods
  toggleView(): void {
    this.posthog.capture('home_toggle_view', {
      currentPosition: this.currentViewPositionSignal(),
      isAtTop: this.isAtTopPosition()
    });
    if (!this.wrapperListScroll?.nativeElement) return;

    console.log("this.isDesktopViewport", this.isDesktopViewport());

    // Hide filters when toggling view
    if (!this.isDesktopViewport())
      this.recyclingFiltersComponent.closeFilter();
    /*     if (this.isFiltersExpanded) {
          this.isFiltersExpanded = false;
          // Optionally blur the search input to close any open filter panels
          if (this.recyclingFiltersComponent) {
            this.recyclingFiltersComponent.toggleFilters();
          }
        } */
    const scrollElement = this.wrapperListScroll.nativeElement;
    const topPosition = this.calculateTopPosition();

    // iOS Safari scroll fix: force reflow and use requestAnimationFrame
    const scrollToPosition = (element: HTMLElement, position: number) => {
      // Force a reflow to ensure iOS recognizes the scroll container
      void element.offsetHeight;

      requestAnimationFrame(() => {
        element.scrollTop = position;
        // Double-set for iOS Safari reliability
        setTimeout(() => {
          element.scrollTop = position;
        }, 0);
      });
    };

    if (this.isAtTopPosition()) {
      // Currently at top, move to bottom (show map)
      scrollToPosition(scrollElement, 0);
      this.currentViewPositionSignal.set('bottom');
      console.log('🗺️ Toggling to map view (bottom position)');
    } else {
      // Currently at bottom, move to top (show list)
      scrollToPosition(scrollElement, topPosition);
      this.currentViewPositionSignal.set('top');

      // Unlock scroll after moving to top and restore inner scroll position
      setTimeout(() => {
        this.checkScrollBehavior();

        // Restore the inner scroll position to avoid unwanted jumping to top
        /*         if (listElement && currentInnerScrollTop > 0) {
                  listElement.scrollTop = currentInnerScrollTop;
                  console.log('🔄 Restored inner scroll position:', currentInnerScrollTop);
                } */
      }, 100); // Slightly longer timeout to ensure scroll behavior is set

      console.log('📋 Toggling to list view (top position)', topPosition);
    }
  }

  isAtTopPosition(): boolean {
    return this.currentViewPositionSignal() === 'top';
  }

  /**
   * Handle tab bar click when already on home tab
   * - If at bottom (map view): toggle to top (list view)
   * - If at top but inner list is scrolled: scroll inner list to top
   * - If at top and inner list is at top: toggle to bottom (map view)
   */
  handleHomeTabClick(): void {
    if (!this.isAtTopPosition()) {
      // At bottom (map view), toggle to show list
      this.toggleView();
      return;
    }

    // At top (list view), check if inner list is scrolled
    const listElement = this.listScrollContainer?.nativeElement;
    const innerScrollTop = listElement?.scrollTop || 0;

    if (innerScrollTop > 10) {
      // Inner list is scrolled, scroll it to top
      console.log('📜 Scrolling inner list to top from position:', innerScrollTop);
      listElement?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Inner list is at top, toggle to map view
      this.toggleView();
    }
  }

  // DEPRECATED: Use toggleButtonText() computed signal instead
  getToggleButtonText(): string {
    return this.toggleButtonText();
  }

  // Map context menu methods
  async showMapContextMenu(coordinates: { lat: number; lng: number }): Promise<void> {
    // new Notification('Título', { body: 'Mensaje' });

    this.posthog.capture('home_map_context_menu', {
      coordinates: coordinates
    });
    console.log('📍 Showing map context menu at coordinates:', coordinates);

    // Define map-specific context menu options
    const menuOptions: ContextMenuOption[] = [
      {
        id: 'createPoint',
        label: this.t.instant('home.createPointHere'),
        iconSrc: 'assets/icons/plus.svg',
        type: 'primary'
      },
      {
        id: 'setLocation',
        label: this.t.instant('home.setAsLocation'),
        iconSrc: 'assets/icons/locate-outline.svg'
      },
      {
        id: 'navigate',
        label: this.t.instant('home.howToGetThere'),
        iconSrc: 'assets/icons/googlemaps.svg'
      },
      {
        id: 'shareLocation',
        label: this.t.instant('home.shareLocation'),
        iconSrc: 'assets/icons/share.svg'
      }
    ];

    const popover = await this.customPopoverService.create({
      component: ContextMenuComponent,
      componentProps: {
        title: this.t.instant('home.mapOptions'),
        options: menuOptions,
        data: coordinates
      },
      cssClass: 'context-menu-popover',
      layer: ScreenLayer.Home,
      modalDomain: ModalDomain.ContextMenu,
      showBackdrop: true,
      backdropDismiss: true
    });

    const { data, role } = await popover.onDidDismiss();

    if (role === 'select' && data) {
      await this.handleMapContextMenuAction(data.action, coordinates);
    }
  }

  private async handleMapContextMenuAction(action: string, coordinates: { lat: number; lng: number }): Promise<void> {
    console.log('🎬 Map context menu action:', action, coordinates);

    switch (action) {
      case 'createPoint':
        await this.pointCreationService.startCreationFlow(coordinates);
        break;

      case 'setLocation':
        console.log('Setting user location to:', coordinates);
        // Use LocationService to set location with reverse geocoding
        await this.locationService.setLocationFromCoordinates(coordinates.lat, coordinates.lng);
        break;

      case 'navigate':
        console.log('Opening navigation to:', coordinates);
        const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
        this.platform.windowOpen(mapsUrl, '_blank');
        break;

      case 'shareLocation':
        console.log('Sharing location:', coordinates);
        const shareUrl = `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`;
        if (navigator.share) {
          navigator.share({
            title: this.t.instant('home.sharedLocation'),
            text: this.t.instant('home.locationLabel', { location: `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}` }),
            url: shareUrl
          });
        } else {
          // Fallback: copy to clipboard
          navigator.clipboard.writeText(shareUrl);
          console.log('Location URL copied to clipboard');
        }
        break;
    }
  }

  ionViewWillLeave() {
    // no destruye
  }

  ionViewDidLeave() {
    // no destruye
  }

  /**
   * Start monitoring map point to detect city changes
   */
  private startMapPointMonitoring(): void {
    // Check every 2 seconds if map point city differs from selected location
    this.checkMapPointInterval = setInterval(() => {
      this.checkMapPointCity();
    }, 2000);
  }

  /**
   * Check if map point city differs from selected location city
   */
  private async checkMapPointCity(): Promise<void> {
    const mapPoint = this.geocodingService.getLastMapPoint();
    const selectedCity = this.locationService.locationCity();

    if (!mapPoint || !selectedCity) {
      this.showSearchHereButton.set(false);
      return;
    }

    // Check if the map point has actually changed since last check
    if (this.lastCheckedPoint &&
      Math.abs(this.lastCheckedPoint.lat - mapPoint.lat) < 0.0001 &&
      Math.abs(this.lastCheckedPoint.lng - mapPoint.lng) < 0.0001) {
      // Map hasn't moved, skip the check
      return;
    }

    // Update last checked point
    this.lastCheckedPoint = { lat: mapPoint.lat, lng: mapPoint.lng };

    // Get city from map point coordinates using reverse geocoding
    try {
      const result = await this.geocodingService.reverseGeocode({
        lat: mapPoint.lat,
        lng: mapPoint.lng
      });

      if (result?.address) {
        const mapPointCity = result.address.city || result.address.town || result.address.village || '';
        this.geocodingService.setMapPointCity(mapPointCity);

        // Compare cities (case-insensitive)
        const isDifferentCity = mapPointCity.toLowerCase() !== selectedCity.toLowerCase();
        this.showSearchHereButton.set(isDifferentCity && mapPointCity !== '');
      }
    } catch (error) {
      console.error('Error checking map point city:', error);
    }
  }

  /**
   * Save previous location to localStorage
   */
  private savePreviousLocation(): void {
    const currentLocation = this.userLocation();
    const currentCity = this.locationService.locationCity();
    const currentName = this.locationService.locationName();

    if (currentLocation) {
      const previousLocation = {
        location: currentLocation,
        city: currentCity,
        name: currentName,
        savedAt: new Date().toISOString()
      };

      try {
        // Save to storage
        this.storage.set('cache', STORAGE_KEYS.cache.previousLocation, previousLocation);
        console.log('💾 Previous location saved:', previousLocation);

        // Also maintain a history (keep last 5)
        const history = this.storage.get<any[]>('cache', STORAGE_KEYS.cache.locationHistory, []);

        history.unshift(previousLocation);
        const limitedHistory = history.slice(0, 5);

        this.storage.set('cache', STORAGE_KEYS.cache.locationHistory, limitedHistory);
      } catch (error) {
        console.error('Error saving previous location:', error);
      }
    }
  }

  /**
   * Handle "Buscar acá" button click
   */
  async onSearchHereClick(): Promise<void> {
    this.triggerVibration();

    const mapPoint = this.geocodingService.getLastMapPoint();
    if (!mapPoint) return;

    // Save previous location before changing
    this.savePreviousLocation();

    // Set new location from map point
    await this.locationService.setLocationFromCoordinates(mapPoint.lat, mapPoint.lng);

    // Hide the button
    this.showSearchHereButton.set(false);

    // Reset to first page
    this.currentPage.set(1);

    // Load nearby points from the new location
    this.loadNearbyPoints(mapPoint.lat, mapPoint.lng);

    // Update map to point on new location if map component exists
    if (this.mapComponent) {
      this.mapComponent.updateUserLocationMarker(mapPoint);
    }

    console.log('📍 Location changed to map point:', mapPoint);
  }

  /**
   * Load nearby recycling points from a specific location
   */
  private loadNearbyPoints(lat: number, lng: number, radius?: number): void {
    const searchRadius = radius || this.NEARBY_SEARCH_RADIUS_KM;
    console.log(`📍 Loading nearby points from location: ${lat}, ${lng} (radius: ${searchRadius}km)`);

    this.pointsApiService.getPointsNearby({ latitude: lat, longitude: lng, radius: searchRadius })
      .subscribe({
        next: (points) => {
          console.log(`✅ Loaded ${points.length} nearby points`);

          // Update the recycling points signal with nearby results
          this.recyclingPointsSignal.set(points as any[]);
          this.updatePointSignals();

          // Reset to first page
          this.currentPage.set(1);

          this.posthog.capture('home_nearby_points_loaded', {
            pointCount: points.length,
            latitude: lat,
            longitude: lng,
            radius: searchRadius
          });
        },
        error: (error) => {
          console.error('❌ Failed to load nearby points:', error);
          // Fallback: keep existing points loaded
          this.posthog.capture('home_nearby_points_error', {
            error: error.message,
            latitude: lat,
            longitude: lng
          });
        }
      });
  }

  // ── Location-based reload ("Buscar acá" in the list) ──

  /**
   * Check if the current viewport center has moved significantly from the last loaded location.
   * Triggers when center shifted > 30% of the previous radius.
   */
  private hasViewportMovedSignificantly(
    prev: { lat: number; lng: number; radiusKm: number },
    curr: { lat: number; lng: number; radiusKm: number },
  ): boolean {
    // Threshold: 30% of the previous radius, converted to degrees (~111.32 km/deg)
    const thresholdDeg = (prev.radiusKm * 0.3) / 111.32;

    return (
      Math.abs(curr.lat - prev.lat) > thresholdDeg ||
      Math.abs(curr.lng - prev.lng) > thresholdDeg
    );
  }

  /**
   * "Buscar acá" handler for the LIST — reloads points from API using current map center + radius.
   * Points come back sorted by distance from center (backend).
   * Different from onSearchHereClick() which changes user location.
   */
  async onSearchInViewportClick(): Promise<void> {
    this.showSearchHereButton.set(false);
    this.isLoadingPoints.set(true);
    this.currentPage.set(1);

    try {
      await this.loadPointsFromApi();
    } finally {
      this.isLoadingPoints.set(false);
    }
  }
}
